import type { NamespaceDesktopWebTranslation } from '../../i18n-types';

const de_desktop_web = {
  eligiblePayers: {
    title: 'Berechtigte Kostenträger',
    subtitle:
      'Bitte überprüfen Sie den Versicherungsschutz des Patienten, bevor Sie eine eConsult einreichen.',
    payerCount: '{count} Kostenträger',
  },
  activityTracker: {
    title: 'Sie werden in Kürze abgemeldet',
    description:
      'Sie waren {amountOfMinutes} Minuten inaktiv. Bewegen Sie die Maus, um angemeldet zu bleiben.',
  },
  annotations: {
    annotationList: {
      noDataInTable: 'Keine Daten verfügbar',
      startAnnotating: 'Mit Annotation beginnen',
      tableHeader: {
        diagnosis: 'Diagnose',
        difficulty: 'Schwierigkeit',
        location: 'Ort',
        status: 'Status',
      },
    },
    annotationsPage: {
      successfullySkippedCase: 'Fall erfolgreich übersprungen',
      successfullyAnnotatedCase: 'Fall erfolgreich annotiert',
      somethingWentWrongCreatingAnnotation:
        'Annotation konnte nicht gespeichert werden, ein Fehler ist aufgetreten.',
      leftSideContent: {
        annotations: 'Annotationen',
        answers: 'Antworten',
        backToList: 'Zur Fallliste zurückkehren',
        diagnosis: 'Diagnose',
        difficulty: 'Schwierigkeit',
        fetchingFormerRaters: 'Frühere Bewerter werden geladen',
        formerRaters: 'Frühere Bewerter',
        genderInTheirAge:
          '{gender|{Female: Frau, *: Mann}} in {pronoun|{Male: seinem, Female: ihrem, *: ihrem}} {age}ern',
        noFormerReviews: 'Keine früheren eKonsile',
      },
      roi: {
        button: 'ROI',
        title: 'ROI',
      },
      toolbar: {
        addTool: '{tool|{arrow:Pfeile, pen:Flächen}} hinzufügen',
        annotationCriteriaLabel: '{criteriaName} annotieren',
        annotations: 'Annotationen',
        approve: 'Genehmigen',
        area: 'Fläche{{n}}',
        arrow: 'Pfeil{{e}}',
        arrowTool: 'Pfeil',
        cancel: 'Abbrechen',
        clickToAnnotate:
          'Klicken Sie auf das Bild, um neue {tool|{arrow:Pfeile, pen:Flächen}} hinzuzufügen',
        clinicalImage: 'Übersichtsbild',
        collapseText: 'Fertig',
        criteriaMultiSelect: 'Mehrfachauswahl',
        criteriaPlaceholder: 'Weitere Kriterien suchen',
        criteriaSearchLabel: 'Weitere Kriterien hinzufügen',
        criteriaSelectLabel: 'Kriterien für {diagnosisName}',
        dermoscopicImage: 'Dermatoskopisches Bild',
        edit: 'Bearbeiten',
        editorNoteLabel: 'Allgemeine Notiz',
        editorNotePlaceholder: 'Notiz hier eingeben...',
        editorNotePlaceholderReadonly: 'Keine Notiz',
        feedbackLabel: 'Feedback',
        feedbackNotePlaceholder: 'Feedback hier eingeben...',
        feedbackNotePlaceholderReadonly: 'Kein Feedback',
        markAsRead:
          '{markedAsRead|{markedAsRead:Gelesen, notMarkedAsRead:Als gelesen markieren}}',
        penTool: 'Stift',
        requestChanges: 'Änderungen anfordern',
        save: 'Speichern',
        skip: 'Überspringen',
        title: 'Fortschritt',
        unApprove: 'Genehmigung zurückziehen',
      },
    },
    tags: {
      annotated: 'Annotiert',
      approved: 'Genehmigt',
      changesRequested: 'Änderungen angefordert',
      disapproved: 'Abgelehnt',
      newFeedback: 'Neues Feedback',
      pending: 'Ausstehend',
      skipped: 'Übersprungen',
    },
  },
  administration: {
    navigationSidebar: {
      users: 'Benutzer',
      eConsults: 'eKonsile',
      title: 'Verwaltung',
      specialists: 'Dermatologen',
      sites: 'Standorte',
      networks: 'Netzwerke',
      federationSettings: 'Föderationseinstellungen',
      forms: 'Formulare',
      specialities: 'Fachgebiete',
      announcements: 'Ankündigungen',
      consents: 'Einwilligungen',
      globalLabel: 'Global',
    },
    federationSelector: {
      chooserTitle: 'Föderation auswählen',
      searchPlaceholder: 'Föderationen durchsuchen',
      noFederations: 'Keine Föderationen gefunden',
      noSearchMatches: 'Keine Föderationen entsprechen Ihrer Suche',
      loadError: 'Föderationen konnten nicht geladen werden',
      retry: 'Erneut versuchen',
    },
  },
  formsPage: {
    title: 'Formularvorlagen',
    slots: {
      'oral-triage': 'Orale Läsion — Triage',
      'oral-tele-assessment': 'Orale Läsion — Facharztbeurteilung',
      'cervical-triage': 'Zervixerkrankung — Triage',
      'cervical-tele-assessment': 'Zervixerkrankung — Facharztbeurteilung',
      'shared-response-review':
        'Orale Läsion und Zervixerkrankung — Bewertung der Antwort',
    },
    columns: {
      form: 'Formular',
      version: 'Version',
      formLanguageVersion: 'Formularsprachversion',
      lastPublished: 'Zuletzt veröffentlicht',
      updater: 'Aktualisiert von',
      actions: 'Aktionen',
    },
    notPublishedYet: 'Noch nicht veröffentlicht',
    noTemplates: 'Keine registrierten Formularvorlagen',
    upload: 'JSON hochladen',
    uploadFor: 'JSON für {form} hochladen',
    confirm: {
      title: 'Veröffentlichte Vorlage überschreiben?',
      description:
        'Dadurch wird das aktuell veröffentlichte JSON für {form} ersetzt.',
      action: 'Vorlage überschreiben',
    },
    publishSuccess: '{form} wurde erfolgreich veröffentlicht.',
    loadError:
      'Formularvorlagen konnten nicht geladen werden. Erneut versuchen.',
    errors: {
      malformedJson: 'Die ausgewählte Datei ist kein gültiges JSON: {message}',
      validationFailed: 'Die Vorlage konnte nicht validiert werden:',
      requestTooLarge:
        'Die serialisierte Upload-Anfrage ist zu groß. Sie muss unter 100 kB bleiben.',
      readFailed: 'Die ausgewählte Datei konnte nicht gelesen werden.',
      publishFailed:
        'Die Formularvorlage konnte nicht veröffentlicht werden. Erneut versuchen.',
      alreadyCreated:
        'Jemand anderes hat dieses Formular zuerst veröffentlicht. Die Liste wurde aktualisiert — laden Sie erneut hoch, um eine neue Version zu veröffentlichen.',
      assignedElsewhere:
        'Veröffentlicht, aber einer ihrer Erkrankungsarten ist für dieses Formular bereits eine andere Vorlage zugewiesen; diese wurde nicht geändert.',
      assignFailed:
        'Veröffentlicht, aber sie konnte nicht allen Erkrankungsarten zugewiesen werden. Laden Sie sie erneut hoch, um es noch einmal zu versuchen.',
    },
  },
  announcementsPage: {
    title: 'Ankündigungen',
    description:
      'Nutzer sehen jeweils eine Ankündigung: zuerst kritisch, dann Warnung, Info und Erfolg. Bei Gleichstand gewinnt die neueste.',
    create: 'Ankündigung erstellen',
    noData: 'Noch keine Ankündigungen',
    noMatches: 'Keine Ankündigungen passen zu diesen Filtern',
    noDataDescription:
      'Ankündigungen, die Sie für diese Föderation erstellen, erscheinen hier.',
    loadError: 'Die Ankündigungen konnten nicht geladen werden.',
    columns: {
      title: 'Titel',
      type: 'Typ',
      status: 'Status',
      audience: 'Zielgruppe',
      statistics: 'Statistik',
      period: 'Zeitraum (UTC)',
    },
    allPages: 'Alle Seiten',
    types: {
      info: 'Info',
      warning: 'Warnung',
      success: 'Erfolg',
      critical: 'Kritisch',
    },
    statuses: {
      draft: 'Entwurf',
      scheduled: 'Geplant',
      active: 'Aktiv',
      expired: 'Abgelaufen',
    },
    shadowed:
      'Verdeckt von „{title}“ für eine überlappende Zielgruppe, {period} (UTC)',
    audience: {
      allUsers: 'Alle Nutzer',
      federationManager: 'Föderationsmanager',
      roles: '{count} Rolle{{n}}',
      jurisdictionalGroups: '{count} Netzwerk{{e}}',
      medicalUnits: '{count} Einrichtung{{en}}',
      rolesIn: '{roles} in {scopes}',
    },
    statistics: {
      estimatedReach: 'geschätzte Reichweite',
      impressions: 'Aufrufe',
      uniqueUsers: 'Nutzer',
      dismissals: 'Ausgeblendet',
      linkOpens: 'Linkklicks',
      note: 'Zählt nur Nutzer mit aktivierter Produktanalyse.',
    },
    period: {
      untilRemoved: 'Bis zur Entfernung',
      from: 'Von {date}',
      to: 'Bis {date}',
    },
    actions: {
      edit: 'Bearbeiten',
      duplicate: 'Duplizieren',
      preview: 'Vollständige Vorschau',
      delete: 'Löschen',
    },
    filters: {
      clear: 'Zurücksetzen',
      selected: 'Ausgewählt',
      all: 'Alle',
    },
    deleteDialog: {
      title: 'Ankündigung löschen?',
      description:
        '„{title}“ wird aus der Liste und aus dem Banner aller Nutzer entfernt.',
      confirm: 'Löschen',
      deleted: 'Ankündigung gelöscht',
      failed: 'Die Ankündigung konnte nicht gelöscht werden.',
    },
    preview: {
      title: 'Vollständige Vorschau',
      description: 'So sehen Nutzer die Ankündigung oben auf jeder Seite.',
    },
    sheet: {
      createTitle: 'Ankündigung erstellen',
      editTitle: 'Ankündigung bearbeiten',
      steps: {
        target: 'Ziel',
        audience: 'Zielgruppe',
        content: 'Inhalt',
        review: 'Überprüfen',
      },
      federations: {
        label: 'Föderationen',
        selectAll: 'Alle auswählen',
        helper: 'Jede ausgewählte Föderation erhält ihre eigene Ankündigung.',
        readOnly:
          'Eine Ankündigung bleibt in der Föderation, für die sie erstellt wurde.',
      },
      audience: {
        everyone: 'Alle',
        narrow: 'Nach Rollen und Orten eingrenzen',
        allRoles: 'Alle Rollen',
        roles: 'Rollen',
        scopes: 'Wo',
        scopesHelper: 'Keine Auswahl bedeutet die ganze Föderation.',
        jurisdictionalGroups: 'Netzwerke',
        medicalUnits: 'Einrichtungen',
        searchJurisdictionalGroups: 'Netzwerke suchen',
        searchMedicalUnits: 'Einrichtungen suchen',
        removedScope: 'Existiert nicht mehr',
        loadError:
          'Die Netzwerke und Einrichtungen dieser Föderation konnten nicht geladen werden.',
      },
      content: {
        type: 'Typ',
        typeDescriptions: {
          info: 'Allgemeine Information.',
          warning: 'Etwas, das Nutzer einplanen sollten.',
          success: 'Etwas, das gut gelaufen oder behoben ist.',
          critical: 'Ein aktives klinisches oder technisches Risiko.',
        },
        typeHelper: 'Legt die Farbe fest und ob Nutzer sie ausblenden können.',
        dismissible: 'Ausblendbar',
        minimiseOnly: 'Nur minimierbar',
        language: 'Inhalt auf {language}',
        languageHelper: 'Für {federations}.',
        title: 'Titel',
        body: 'Nachricht',
        linkLabel: 'Linktext',
        linkUrl: 'Link-URL',
        linkHelper: 'Optional. Ein Link braucht einen Text und eine URL.',
        linkLabelCount: '{count}/{max}',
        preview: 'Vorschau',
        previewNote: 'So erscheint die Ankündigung nach der Veröffentlichung.',
      },
      review: {
        schedule: 'Zeitplan',
        startsAt: 'Aktiv ab (UTC)',
        endsAt: 'Aktiv bis (UTC)',
        date: 'Datum',
        time: 'Uhrzeit',
        pickDate: 'Datum wählen',
        untilRemoved: 'Bis zur Entfernung (kein Enddatum)',
        summary: 'Es werden {count} separate Ankündigung{{en}} erstellt:',
        summaryEdit: 'Diese Ankündigung gehört zu {federation}.',
        independent: 'Jede wird einzeln bearbeitet, gelöscht und gezählt.',
      },
      errors: {
        federations: 'Wählen Sie mindestens eine Föderation.',
        removedScope:
          'Entfernen Sie die Markierung bei Netzwerken und Einrichtungen, die nicht mehr existieren.',
        narrowEmpty:
          'Wählen Sie mindestens eine Rolle, ein Netzwerk oder einen Standort.',
        required: 'Erforderlich.',
        plainText: 'Verwenden Sie reinen Text ohne HTML-Tags.',
        linkPair: 'Ein Link braucht einen Text und eine URL.',
        linkUrl:
          'Geben Sie eine vollständige Webadresse ein, z. B. https://example.com',
        tooLong: 'Höchstens {max} Zeichen.',
        endAfterStart: 'Das Ende muss nach dem Beginn liegen.',
        endInPast: 'Das Ende liegt bereits in der Vergangenheit.',
      },
      back: 'Zurück',
      next: 'Weiter',
      save: 'Speichern',
      publish: '{count} Ankündigung{{en}} veröffentlichen',
      saveDraft: 'Entwurf speichern',
      created: '{count} Ankündigung{{en}} erstellt',
      saved: 'Ankündigung gespeichert',
      failed: 'Die Ankündigung konnte nicht gespeichert werden.',
    },
  },
  specialitiesPage: {
    iconMissingFromWebKit:
      'Fehlt im Web-Icon-Kit und wird daher auf dem Desktop nicht angezeigt. Laden Sie es zuerst in dieses Font-Awesome-Kit hoch.',
    iconMissingFromMobileKit:
      'Fehlt im Mobil-Icon-Kit und wird daher in Capture nicht angezeigt. Laden Sie es in dieses Font-Awesome-Kit hoch und veröffentlichen Sie die Apps zuerst.',
    back: 'Zurück',
    info: 'Fachgebiet',
    editConditionType: '{name} bearbeiten',
    conditionTypeNameLocked:
      'Der englische Name identifiziert die Befundart gegenüber den Apps und kann hier nicht geändert werden.',
    conditionTypeCreated: 'Befundart erstellt.',
    conditionTypeUpdated: 'Befundart aktualisiert.',
    noConditionTypes: 'Noch keine Befundarten',
    notVisibleYet:
      'Dieses Fachgebiet erscheint erst in den Apps, wenn es mindestens eine Befundart hat.',
    title: 'Fachgebiete',
    addSpeciality: 'Fachgebiet hinzufügen',
    editSpeciality: 'Fachgebiet bearbeiten',
    noSpecialities: 'Noch keine Fachgebiete',
    loadError:
      'Die Fachgebiete konnten nicht geladen werden. Erneut versuchen.',
    conditionTypesLoadError:
      'Die Befundarten konnten nicht geladen werden. Erneut versuchen.',
    noIcon: 'Kein Symbol',
    positionBefore: '{position} — vor {item}',
    positionLast: '{position} — zuletzt',
    positionPushesDown:
      '{item} und alles darunter rückt eine Position nach unten.',
    positionAtEnd: 'Wird am Ende der Liste hinzugefügt.',
    conditionTypes: 'Befundarten',
    addConditionType: 'Befundart hinzufügen',
    createSuccess: 'Fachgebiet erstellt.',
    updateSuccess: 'Fachgebiet aktualisiert.',
    columns: {
      icon: 'Symbol',
      name: 'Name',
      nameDa: 'Dänischer Name',
      nameDe: 'Deutscher Name',
      conditionTypes: 'Befundarten',
      ordering: 'Listenposition',
    },
    fields: {
      name: 'Name (Englisch)',
      nameDa: 'Name (Dänisch)',
      nameDe: 'Name (Deutsch)',
      icon: 'Symbol',
      iconPlaceholder: 'Symbol auswählen',
      ordering: 'Listenposition',
    },
    errors: {
      nameRequired: 'Namen eingeben',
      conditionTypeNameRequired: 'Namen der Befundart eingeben',
      duplicateName: 'Dieser Name wird bereits verwendet.',
      saveFailed:
        'Das Fachgebiet konnte nicht gespeichert werden. Erneut versuchen.',
      conditionTypeSaveFailed:
        'Die Befundart konnte nicht gespeichert werden. Erneut versuchen.',
    },
  },
  federationPage: {
    pageTitle: 'Einstellungen',
    loadError: 'Die Einstellungen der Föderation konnten nicht geladen werden.',
    generalSettings: {
      title: 'Allgemeine Einstellungen',
      locale: 'Sprache',
      localeDescription: 'Legt die Standardsprache für diese Föderation fest.',
      localePlaceholder: 'Sprache auswählen',
      localeOptions: {
        en: 'Englisch',
        da: 'Dänisch',
        de: 'Deutsch',
      },
      requireManualRelease: 'Manuelle Freigabe von Fällen aktivieren',
      requireManualReleaseDescription:
        'Wenn aktiviert, müssen Fälle vom Verwaltungspersonal manuell freigegeben werden, bevor sie an einen Spezialisten gesendet werden.',
      reassignmentHours: 'Weiterverteilung von Fällen (Stunden)',
      reassignmentHoursDescription:
        'Gibt an, wie viele Stunden vergehen, bevor ein unbeantworteter Fall automatisch einem anderen Spezialisten zugewiesen wird.',
      reassignmentHoursPlaceholder: 'Z.B. 12',
      referralNotificationEmail:
        'Benachrichtigungs-E-Mail für neue Überweisungen',
      referralNotificationEmailDescription:
        'E-Mail-Adresse, die eine Benachrichtigung erhält, sobald ein neues Telekonsil in dieser Föderation erstellt wird.',
      referralNotificationEmailPlaceholder: 'email@beispiel.de',
      claimReferral: 'Telekonsil beanspruchen',
      claimReferralDescription:
        'Ermöglicht es Spezialisten, ein Telekonsil zu beanspruchen, bevor sie antworten. Wird verwendet, wenn mehrere Spezialisten eine Klinik teilen, um zu verhindern, dass zwei Spezialisten auf denselben Fall antworten.',
      hideEconsultIdentifier: 'Telekonsil-Kennung verbergen',
      hideEconsultIdentifierDescription:
        'Verbirgt die Telekonsil-Kennung in der Benutzeroberfläche.',
      imageQualityAssessment: 'Bildqualitätsbewertung',
      imageQualityAssessmentDescription:
        'Ermöglicht es dem Spezialisten, die Qualität der vom überweisenden Arzt aufgenommenen Bilder zu bewerten.',
      enableEconsultNotifications: 'Telekonsil-Benachrichtigungen aktivieren',
      enableEconsultNotificationsDescription:
        'Aktiviert oder deaktiviert E-Mail-Benachrichtigungen an Hausärzte, wenn ein neues Telekonsil von einem Spezialisten bewertet wurde.',
      econsultQuestionSummary: 'Telekonsil-Frage und/oder Fallzusammenfassung',
      econsultQuestionSummaryDescription:
        'Legt fest, ob das Freitextfeld für Frage oder Fallzusammenfassung für den Hausarzt beim Erstellen eines Telekonsils verpflichtend auszufüllen ist.',
      lesions: {
        title: 'Läsionen',
        description:
          'Läsionen sind standardmäßig immer aktiviert. Dieser Workflow kann nicht deaktiviert werden.',
      },
      rashes: {
        title: 'Hautausschläge',
        description:
          'Aktivieren Sie dies, um Benutzern die Registrierung von Hautausschlägen und den Zugang zum vollständigen Hautausschlag-Workflow in Dermloop zu ermöglichen.',
      },
      priorSpecialistAssignmentLookbackDays:
        'Rückblickzeitraum für Spezialistenzuweisung (Tage)',
      priorSpecialistAssignmentLookbackDaysDescription:
        'Anzahl der Tage, die beim Erstellen einer neuen Überweisung für einen Patienten zurückgeblickt wird. Das System prüft, welcher Spezialist frühere Fälle dieses Patienten innerhalb des Rückblickzeitraums beantwortet hat, um die Kontinuität der Versorgung durch Zuweisung desselben Spezialisten sicherzustellen.',
      userAffiliationOccupationAndSpecialisation:
        'Beruf und Fachrichtung aktivieren',
      userAffiliationOccupationAndSpecialisationDescription:
        'Zeigt den Beruf und die Fachrichtung des Benutzers in der Patientenakte und in einigen Fällen in der rechten Seitenleiste an.',
      showDatetimeOnTeleEvaluation:
        'Datum und Uhrzeit der Teledermatologen-Bewertung anzeigen',
      showDatetimeOnTeleEvaluationDescription:
        'Zeigt dem überweisenden Arzt Datum und Uhrzeit der Bewertung des Teledermatologen an.',
      enableTeleAssessmentRating:
        'Bewertung der Spezialistenbeurteilung aktivieren',
      enableTeleAssessmentRatingDescription:
        'Ermöglicht es Klinikern, die Qualität der Beurteilung eines Spezialisten anonym zu bewerten. Bewertungen sind für den Föderationsadministrator sichtbar.',
      timeSpentReviewingTeleassessments:
        'Zeiterfassung für Spezialistenbewertungen aktivieren',
      timeSpentReviewingTeleassessmentsDescription:
        'Fordert Spezialisten auf, die für die Überprüfung jeder Beurteilung aufgewendete Zeit zu protokollieren.',
      federationNameInPdfTitle: 'Föderationsname im PDF-Titel aktivieren',
      federationNameInPdfTitleDescription:
        'Fügt den Föderationsnamen in den Titel von PDF-Berichten ein, die aus der Patientenakte generiert werden.',
      inactivityDeactivationDays: 'Inaktivitätstage bis zur Deaktivierung',
      inactivityDeactivationDaysDescription:
        'Anzahl der Tage, die ein Benutzer inaktiv sein kann, bevor das Konto deaktiviert wird.',
    },
    patientDataSettings: {
      title: 'Patientendateneinstellungen',
      patientIdentifier: 'Patientenkennung',
      patientIdentifierDescription:
        'Definiert, welcher Typ von Patientenkennung in dieser Föderation verwendet wird.',
      patientIdentifierPlaceholder: 'Kennung auswählen',
      patientIdentifierOptions: {
        ssn: 'Sozialversicherungsnummer',
        ssnDescription:
          'Verwendet die Sozialversicherungsnummer als primäre Patientenkennung.',
        mrn: 'Krankenaktennummer',
        mrnDescription:
          'Verwendet die Krankenaktennummer als primäre Patientenkennung.',
      },
      hidePatientIdentifier: 'Patientenkennung verbergen',
      hidePatientIdentifierDescription:
        'Verbirgt die Patientenkennung in der Benutzeroberfläche in allen Ansichten.',
      hidePatientName: 'Patientenname verbergen',
      hidePatientNameDescription:
        'Verbirgt den Patientennamen in der Benutzeroberfläche in allen Ansichten.',
      patientBlockPolicy: 'Patientensperr-Richtlinie',
      patientBlockPolicyDescription:
        'Steuert, wie Patientendaten von außerhalb der Klinik eines Benutzers behandelt werden.',
      patientBlockPolicyOptions: {
        noBlock: 'Keine Sperre',
        noBlockDescription:
          'Benutzer können ohne Einschränkungen auf Patientenakten außerhalb ihres Standorts zugreifen.',
        softBlock: 'Weiche Sperre',
        softBlockDescription:
          'Benutzer erhalten eine Warnung beim Zugriff auf Patientenakten außerhalb ihres Standorts, der Zugriff ist jedoch weiterhin erlaubt.',
        block: 'Sperre',
        blockDescription:
          'Benutzer können nicht auf Patientenakten außerhalb ihres Standorts zugreifen.',
      },
      patientAddressCapture: 'Erfassung Patientenadresse',
      patientAddressCaptureDescription:
        'Fragt während der Scanner-Registrierung nach Adresse und E-Mail des Patienten, wenn diese fehlen.',
      skinCheckHistoryQuestions: 'Fragen zur Hautuntersuchungs-Historie',
      skinCheckHistoryQuestionsDescription:
        'Stellt in der Erfassung zwei verpflichtende Läsionsfragen zu geplanten und früheren Hautuntersuchungen des Patienten bei einem Arzt.',
    },
    save: 'Speichern',
    federationName: 'Name',
    federationNameDescription: 'Der Anzeigename dieser Föderation.',
    features: {
      title: 'Funktionen',
      productAnalytics: 'Produktanalyse',
      productAnalyticsDescription:
        'Sendet Produktanalyse-Ereignisse aus den angemeldeten Apps für alle in dieser Föderation.',
      enabledElsewhere:
        'Die Produktanalyse ist an anderer Stelle ebenfalls für Teile oder die gesamte Föderation eingeschaltet. Wenn Sie diesen Schalter ausschalten, bleibt sie dort aktiv.',
      loadError: 'Die Funktionen konnten nicht geladen werden.',
      toasts: {
        saved: 'Produktanalyse gespeichert',
        error: 'Produktanalyse konnte nicht gespeichert werden',
      },
    },
    toasts: {
      saved: 'Föderationseinstellungen gespeichert',
      error: 'Föderationseinstellungen konnten nicht gespeichert werden',
    },
  },
  buttons: {
    addAdditionalCriteria: 'Weitere Kriterien hinzufügen',
    approve: 'Genehmigen',
    backToCaseList: 'Zur Fallliste zurückkehren',
    clinicalImages: 'Übersichtsbilder',
    close: 'Schließen',
    closeUpImages: 'Nahaufnahmen',
    compare: 'Vergleichen',
    compareImages: 'Bilder vergleichen',
    copy: 'Kopieren',
    dermoscopicImages: 'Dermatoskopische Bilder',
    dismiss: 'Abschließen',
    editOrTakeAction: 'Bearbeiten',
    inviteUser: 'Benutzer einladen',
    lesionInfo: 'Läsionsinfos',
    login: 'Anmelden',
    loginAsAnotherUser: 'Benutzer wechseln',
    next: 'Weiter',
    nextCase: 'Nächster Fall',
    open: 'Öffnen',
    overviewImages: 'Übersichtsbilder',
    patientInfo: 'Patienteninfos',
    previous: 'Zurück',
    refresh: 'Aktualisieren',
    remove: 'Entfernen',
    removeEmail: '{email} entfernen',
    resetFilters: 'Filter zurücksetzen',
    saveChangesAndClose: 'Speichern und schließen',
    sendInvitation: 'Einladung senden',
    sendTeleRequest: 'eKonsil absenden',
    startAnnotating: 'Annotation starten',
    unapprove: 'Genehmigung zurückziehen',
    updateProfile: 'Profil aktualisieren',
  },
  calendar: {
    rangeFromLabel: 'Von',
    rangeToLabel: 'bis',
  },
  changeRegionAlertDialog: {
    changeRegion: 'Region Ändern',
    changeToEUMessage:
      'Es sieht so aus, als ob Sie sich in Europa befinden, obwohl Sie derzeit unsere US-Website nutzen. Möchten Sie zur Dermloop EU wechseln?',
    changeToUSMessage:
      'Es sieht so aus, als ob Sie sich in den USA befinden, obwohl Sie derzeit unsere EU-Website nutzen. Möchten Sie zur Dermloop US wechseln?',
    stayInEU: 'Auf der EU-Website bleiben',
    stayInUS: 'Auf der US-Website bleiben',
    changeToEU: 'Zur EU-Website wechseln',
    changeToUS: 'Zur US-Website wechseln',
  },
  clinicalActions: {
    addComment: 'Kommentar hinzufügen',
    allowedClinicalActions: 'Erlaubte klinische Handlung',
    otherClinicalAction: 'Andere klinische Handlung',
    requestNewImage: 'Neues Bild anfordern',
    suggestedClinicalAction: 'Vorgeschlagene klinische Handlung',
    rateFollowUpPriority: 'Nachverfolgungspriorität des Falls bewerten',
    followUpPriorityTooltip:
      'Bewerten Sie die Priorität der klinischen Nachverfolgung. Die Prioritätsstufe wird dem überweisenden Arzt mitgeteilt.',

    followUpPriorityNoneLabel: 'Keine',
    accessLevels: {
      basic: 'Basiszugang (nur eKonsile)',
      limited:
        'Regulärer Zugang (eKonsile, Verlaufskontrolle, keine weitere Handlung, neue Bildaufnahme)',
      full: 'Vollzugang (alle Handlungen inkl. Biopsie)',
    },
  },
  clinicalStatistics: {
    action: 'Handlung',
    average: 'Durchschnitt von {medicalUnitName}',
    clinicalAction: 'Klinische Handlung',
    clinicalActions:
      '{type|{referralPhysical: Physische Überweisungen, referralTele: Reguläre Teleüberweisungen, referralTeleUrgent: Dringende Teleüberweisungen, biopsy: Biopsien, plannedBiopsy: Geplante Biopsien, followUp: Verlaufskontrollen, dismissal: Abschließen, newImageRegistration: Neue Bildaufnahme, topicalTreatment: Topische Behandlung, noAction: Keine weitere Handlung}}',
    clinicalStatistics: 'Klinische Statistik',
    diagnosticAccuracy: 'Diagnosegenauigkeit',
    diagnosticValues: 'Diagnosekennzahlen',
    distributionOfClinicalActions:
      'Vergleich der von Ihnen vorgenommenen klinischen Handlungen mit denen von anderen Derloop-Nutzern.',
    falseNegative: 'Falsch negativ',
    falsePositive: 'Falsch positiv',
    forAllTime: 'Gesamter Zeitraum',
    last12Months: 'Letzte 12 Monate',
    last4Weeks: 'Letzte 4 Wochen',
    last7Days: 'Letzte 7 Tage',
    negativePredictiveValue: 'NPW',
    notEnoughData: 'Nicht genügend Nutzer, um Daten anonym anzuzeigen',
    performanceRelatingTo: 'Leistung in Bezug auf',
    positivePredictiveValue: 'PPW',
    sensitivity: 'Sensitivität',
    specificity: 'Spezifität',
    toolTips: {
      diagnosticAccuracy:
        'Wie oft Läsionen korrekt als gutartig oder bösartig eingestuft wurden',
      falseNegative:
        'Anzahl bösartiger Läsionen, die fälschlicherweise als gutartig eingestuft wurden',
      falsePositive:
        'Anzahl gutartiger Läsionen, die fälschlicherweise als bösartig eingestuft wurden',
      negativePredictiveValue:
        'Wie viele als gutartig klassifizierte Läsionen tatsächlich gutartig waren',
      positivePredictiveValue:
        'Wie viele als bösartig klassifizierte Läsionen tatsächlich bösartig waren',
      sensitivity:
        'Wie viele der bösartigen Läsionen als bösartig erkannt wurden',
      specificity:
        'Wie viele der gutartigen Läsionen als gutartig erkannt wurden',
      totalLesions:
        'Nur von Ihnen registrierte Läsionen mit Handlung (z. B. Abschluss, eKonsil oder Biopsie) werden hier angezeigt.',
      trueNegative:
        'Anzahl gutartiger Läsionen, die korrekt als gutartig eingestuft wurden',
      truePositive:
        'Anzahl bösartiger Läsionen, die korrekt als bösartig eingestuft wurden',
    },
    totalLesions: 'Gesamtzahl der Läsionen',
    trainingStatistics: 'Trainingsstatistik',
    trueNegative: 'Richtig negativ',
    truePositive: 'Richtig positiv',
    you: 'Sie',
    yourAbilityToClassifyLesions:
      'Diese Kennzahlen spiegeln Ihre diagnostische Genauigkeit bei der Einordnung von Hautläsionen als gutartig oder bösartig wider. Berücksichtigt werden ausschließlich Daten von Dermloop-Patienten.',
    yourMedicalUnitDistribution: 'Verteilung nach Standort',
    yourPersonalMetrics: 'Ihre Kennzahlen',
  },
  compareImages: {
    header: {
      back: 'Zurück',
      title: 'Bilder vergleichen',
      nImages: '{count} Bild{{count:er}}',
    },
    images: 'Bild{{nrOfImages:er}}',
    imageType:
      '{imageType|{Overview: Übersicht, Close up: Nahaufnahme, Clinical: Klinisch, Follow-up dermoscopy: Follow-up-Dermatoskopie, Dermoscopy: Dermatoskopie}}',
    allImageTypes: 'Alle Bildtypen',
    dragAndDrop: 'Ziehen und ablegen, um Bild anzuzeigen',
    gallery: {
      title: 'Galerie',
      filters: 'Filter',
      close: 'Schließen',
      open: 'Öffnen',
      sortBy: 'Sortieren nach',
      sortMostRecentFirst: 'Neueste zuerst',
      sortOldestFirst: 'Älteste zuerst',
      dateOfRegistration: 'Registrierungsdatum',
      allDates: 'Alle',
    },
    lesionFilter: {
      selectedText: 'Ausgewählte Befunde',
      availableItemsText: 'Alle Befunde',
      clearText: 'Löschen',
    },
    toolbar: {
      rotate: 'Drehen',
      done: 'Fertig',
    },
  },
  imageOverview: {
    flashOff: 'Mit Blitz anzeigen',
    flashOn: 'Ohne Blitz anzeigen',
    showMarker: 'Markierung anzeigen',
    hideMarker: 'Markierung ausblenden',
    downloadImage: 'Bild herunterladen',
    nImages: '{count} Bild{{count:er}}',
  },
  consentDialog: {
    dialogTitle: 'Fehlende Einwilligung',
    dialogDescription:
      'Dies ist eine Liste der erforderlichen Einwilligungen. Erteilen Sie Ihre Einwilligung, indem Sie die gewünschte Einwilligung auswählen und auf „Speichern“ klicken. Die Links führen Sie zum vollständigen Text der Einwilligung.',
    dialogConsentChosen: 'Zeichen ausgewählt',
    dialogConsentAll: 'Unterschreiben Sie alle',
    dialogNameColumn: 'Name',
    dialogLinkColumn: 'Link',
    dialogLinkText: 'Link zur Einwilligung',
    consentError:
      'Bitte wählen Sie die obligatorischen Einwilligungserklärungen aus und unterzeichnen Sie diese.',
  },
  consent: {
    consentList: {
      loadError:
        'Die Einwilligungen konnten nicht geladen werden. Versuchen Sie es erneut.',
      noDataInTable: 'Keine Daten verfügbar',
      header: {
        name: 'Name',
        description: 'Beschreibung',
        mandatory: 'Pflichtfeld',
      },
      title: 'Einwilligungen',
    },
    consentEdit: {
      consent: {
        consentNameLabel: 'Interner Name',
        consentNamePlaceholder: 'Internen Namen eingeben',
        consentDescriptionLabel: 'Interne Beschreibung',
        consentDescriptionPlaceholder: 'Interne Beschreibung eingeben',
        consentMandatoryLabel: 'Pflichtfeld',
        consentExternalConsentLabel: 'Externe Einwilligung',
        consentJGIdsLabel: 'Zuständigkeitsgruppen',
        consentTypeLabel: 'Einwilligungstyp',
        consentTypeTriggerText: 'Auswählen',
        consentTypes:
          '{type|{patient:Patient, medical_unit:Klinik, user: Benutzer}}',
        consentJGTrigger: {
          availableItemsText: 'Zuständigkeitsgruppen',
          clearText: 'Leeren',
          emptyText: 'Keine Gruppen vorhanden',
          placeholder: 'Suchen',
          searchText: 'Zuständigkeitsgruppen suchen',
          selectedText: 'Ausgewählte Gruppen',
          triggerText: 'Auswählen',
        },
      },
      revision: {
        revisionNameLabel: 'Revisionsname',
        revisionNamePlaceholder: 'Revisionsnamen eingeben',
        revisionDescriptionLabel: 'Revisionsbeschreibung',
        revisionDescriptionPlaceholder: 'Revisionsbeschreibung eingeben',
        revisionLongTextLabel: 'Inhalt der Revision',
        revisionLongTextPlaceholder: 'Revisionsinhalt eingeben ...',
        validityHoursLabel:
          'Wie lange ist die Einwilligung gültig? (in Stunden)',
        validityHoursPlaceholder:
          'Anzahl der Stunden, in denen die Einwilligung nach ihrer Erteilung gültig ist.',
      },
      cannotBeChanged: 'Kann nicht geändert werden',
      delete: 'Löschen',
      newConsent: 'Neu',
      onDeleteText: {
        cantDelete: 'Sie müssen die Einwilligung aus allen Gruppen entfernen.',
        deleteError: 'Einwilligung konnte nicht gelöscht werden.',
      },
      onSubmitText: {
        success: 'Einwilligung erfolgreich gespeichert',
        createError: 'Einwilligung konnte nicht erstellt werden',
        updateError: 'Einwilligung konnte nicht aktualisiert werden',
        revisionError:
          'Die Einwilligung wurde gespeichert, die Revision jedoch nicht. Speichern Sie erneut, um sie hinzuzufügen.',
      },
      submit: 'Speichern',
    },
  },
  copyMedicalRecord: {
    registration: 'Registrierung {type}',
    skinType: 'Hauttyp',
    patientWithRash: 'Vorstellung mit Ausschlag bei {type}',
    patientWithLesion: 'Vorstellung mit Läsion bei {type}',
    and: 'und',
    dateConnector: '-',
    telediagnosis: 'Einschätzung Dermatologe',
    differentialDiagnosis: 'Differentialdiagnose',
    primaryDiagnosis: 'Diagnose',
    secondaryDiagnosis: 'Differentialdiagnose',
    tertiaryDiagnosis: 'Zweite Differentialdiagnose',
    tentativeDiagnosis: 'Verdachtsdiagnose',
    recommendedAction: 'Empfehlung',
    note: 'Zusätzliche Details',
    evaluatedBy: 'Befundet von',
    by: 'von',
    additionalDetails: 'Zusätzliche Details',
    plan: 'Plan',
    conditionAge: {
      age: ', besteht seit {age}.',
      always: ', besteht seit Geburt/früher Kindheit.',
      otherEmpty: ', Dauer nicht klar angegeben.',
      unknown: ', unbekannte Dauer.',
      none: '.',
    },
    medication: {
      no: 'Aktuelle Medikamenteneinnahmen und relevante Expositionen werden verneint.',
      yes: 'Mögliche Medikamenten-/Expositionsänderung, keine näheren Details angegeben.',
      unknown: 'Unklar, ob neue Medikamente oder Expositionen vorliegen.',
      reported: 'Vor Beginn der Beschwerden wird von {text} berichtet.',
    },
    symptoms: {
      no: 'Keine begleitenden Symptome.',
      yes: 'Symptome berichtet, aber nicht näher beschrieben.',
      unknown: 'Unklar, ob Symptome vorhanden sind.',
      reported: 'Patient berichtet: {text}.',
      otherSymptoms: 'Patient berichtet: {text}.',
      ulceration: 'Ulzeration',
      bleeding: 'Blutung',
      itching: 'Juckreiz',
      soreness: 'Schmerzen',
    },
    appearanceChanges: {
      no: 'Keine Veränderungen an der Läsion beobachtet.',
      yes: 'Veränderungen an der Läsion beobachtet, aber nicht näher beschrieben,',
      unknown: 'Veränderungen sind unbekannt.',
      reported: 'Veränderungen in {text} beobachtet,',
      otherChanges: 'Der Patient berichtet über Veränderungen: {text},',
      age: {
        selected: 'über {age}.',
        other: 'Veränderungen traten über {age} auf.',
        unknown: 'Der Zeitraum der Veränderungen ist unbekannt.',
      },
    },
    priorLesionTreatment: {
      no: 'Keine vorherige Therapie oder Untersuchung.',
      yes: 'Von vorheriger Therapie berichtet, aber nicht näher beschrieben.',
      unknown:
        'Unbekannt, ob eine vorherige Therapie oder Untersuchung stattfand.',
      reported: 'Vorherige Therapie/Untersuchung: {text}.',
    },
    priorRashTreatment: {
      no: 'Keine vorherige Therapie.',
      yes: 'Von vorheriger Therapie berichtet, aber nicht näher beschrieben.',
      unknown: 'Details über vorherige Therapie sind unbekannt.',
      reported: 'Vorherige Therapie mit {text}.',
    },
    formerSkinCancerAndImmunosuppressantUse: {
      yes_yes:
        'Anamnestisch wird von {cancers} berichtet. Es erfolgte eine Therapie mit {immuno}.',
      yes_unspecified:
        'Anamnestisch wird von {cancers} berichtet. Es erfolgte eine immunsuppressive Therapie, Details wurden nicht weiter beschrieben.{immuno}',
      yes_no:
        'Anamnestisch wird von {cancers} berichtet. Es erfolgte keine immunsuppressive Therapie.{immuno}',
      yes_unknown:
        'Anamnestisch wird von {cancers} berichtet. Es ist unklar, ob eine immunsuppressive Therapie erfolgte.{immuno}',
      yes_unanswered: 'Anamnestisch wird von {cancers}.{immuno} berichtet.',

      unspecified_yes:
        'Anamnestisch wird von Hautkrebs berichtet (keine näheren Angaben). Es erfolgte eine immunsuppressive Therapie einschließlich {immuno}.{cancers}',
      unspecified_unspecified:
        'Anamnestisch wird von Hautkrebs berichtet. Es erfolgte eine immunsuppressive Therapie, weitere Details zu beidem wurde nicht angegeben.{cancers}{immuno}',
      unspecified_no:
        'Anamnestisch wird von Hautkrebs berichtet (keine näheren Angaben). Es erfolgte keine immunsuppressive Therapie.{cancers}{immuno}',
      unspecified_unknown:
        'Anamnestisch wird von Hautkrebs berichtet (keine näheren Angaben). Es ist unklar, ob eine immunsuppressive Therapie erfolgte.{cancers}{immuno}',
      unspecified_unanswered:
        'Anamnestisch wird von Hautkrebs berichtet. Es liegen keine näheren Angaben vor.{cancers}{immuno}',

      no_yes:
        'Keine Hautkrebserkrankung in der Anamnese. Es erfolgte eine Therapie mit {immuno}.{cancers}',
      no_unspecified:
        'Keine Hautkrebserkrankung in der Anamnese. Es erfolgte eine immunsuppressive Therapie (keine näheren Angaben).{cancers}{immuno}',
      no_no:
        'Es besteht keine Anamnese hinsichtlich einer Hautkrebserkrankung oder einer immunsuppressiven Therapie.{cancers}{immuno}',
      no_unknown:
        'Es besteht keine Anamnese hinsichtlich einer Hautkrebserkrankung. Es ist unklar, ob eine immunsuppressive Therapie erfolgte.{cancers}{immuno}',
      no_unanswered:
        'Es besteht keine Anamnese hinsichtlich einer Hautkrebserkrankung.{cancers}{immuno}',

      unknown_yes:
        'Hautkrebsanamnese unklar. Therapie mit {immuno} erfolgt.{cancers}',
      unknown_unspecified:
        'Hautkrebsanamnese unklar. Eine immunsuppressive Therapie ist erfolgt.{cancers}{immuno}',
      unknown_no:
        'Hautkrebsanamnese unklar. Es wird von keiner immunsuppressiven Therapie berichtet.{cancers}{immuno}',
      unknown_unknown:
        'Vorherige Hautkrebserkrankung oder immunsuppressive Therapie unbekannt.{cancers}{immuno}',
      unknown_unanswered: 'Hautkrebsanamnese unklar.{cancers}{immuno}',

      unanswered_yes: 'Es erfolgte eine Therapie mit {immuno}.{cancers}',
      unanswered_unspecified:
        'Es erfolgte eine immunsuppressive Therapie (keine näheren Angaben).{cancers}{immuno}',
      unanswered_no:
        'Keine immunsuppressive Therapie in der Anamnese.{cancers}{immuno}',
      unanswered_unknown:
        'Es ist unklar, ob eine immunsuppressive Therapie erfolgte.{cancers}{immuno}',
    },
    rashDiscomfortAndSystemicSymptoms: {
      yes_yes:
        'Der Ausschlag verursacht {discomfort}, und der Patient berichtet außerdem von {symptoms}.',
      yes_unspecified:
        'Der Ausschlag verursacht {discomfort}, und der Patient berichtet auch über systemische Symptome, jedoch ohne weitere Details.{symptoms}',
      yes_no:
        'Der Ausschlag verursacht {discomfort}, systemische Symptome werden verneint.{symptoms}',
      yes_unknown:
        'Der Ausschlag verursacht {discomfort}. Es ist unklar, ob der Patient systemische Symptome hat.{symptoms}',
      yes_unanswered: 'Der Ausschlag verursacht {discomfort}.{symptoms}',

      unspecified_yes:
        'Der Ausschlag verursacht nicht näher beschriebene lokale Symptome und wird begleitet von {symptoms}.{discomfort}',
      unspecified_unspecified:
        'Der Ausschlag verursacht lokale und systemische Symptome; Details fehlen jedoch.{discomfort}{symptoms}',
      unspecified_no:
        'Der Ausschlag verursacht nicht näher beschriebene lokale Symptome; systemische Symptome werden verneint.{discomfort}{symptoms}',
      unspecified_unknown:
        'Der Ausschlag verursacht nicht näher beschriebene lokale Symptome, es ist jedoch unklar, ob systemische Symptome vorliegen.{discomfort}{symptoms}',
      unspecified_unanswered:
        'Der Ausschlag verursacht nicht näher beschriebene lokale Symptome.{discomfort}{symptoms}',

      no_yes:
        'Der Ausschlag verursacht keine Beschwerden. Es wird jedoch über {symptoms}.{discomfort} berichtet',
      no_unspecified:
        'Der Ausschlag verursacht keine Beschwerden. Allerdings werden systemische Symptome berichtet, die jedoch nicht weiter spezifiziert wurden.{discomfort}{symptoms}',
      no_no:
        'Der Ausschlag verursacht keine Beschwerden und es liegen keine systemischen Symptome vor.{discomfort}{symptoms}',
      no_unknown:
        'Der Ausschlag verursacht keine Beschwerden. Es ist unklar, ob systemische Symptome vorhanden sind.{discomfort}{symptoms}',
      no_unanswered:
        'Der Ausschlag verursacht keine Beschwerden.{discomfort}{symptoms}',

      unknown_yes:
        'Angabe von {symptoms}; das Vorliegen lokaler Symptome ist unklar.{discomfort}',
      unknown_unspecified:
        'Es ist unklar, ob der Ausschlag Beschwerden verursacht. Der Patient berichtet von systemischen Symptomen, die nicht weiter beschrieben wurden.{discomfort}{symptoms}',
      unknown_no:
        'Es ist unklar, ob der Ausschlag Beschwerden verursacht, aber der Patient gibt keine systemischen Symptome an.{discomfort}{symptoms}',
      unknown_unknown:
        'Es ist unklar, ob der Ausschlag lokale oder systemische Symptome verursacht.{discomfort}{symptoms}',
      unknown_unanswered:
        'Es ist unklar, ob der Ausschlag Beschwerden verursacht.{discomfort}{symptoms}',

      unanswered_yes: 'Es wird über {symptoms}.{discomfort} berichtet',
      unanswered_unspecified:
        'Der Patient berichtet über systemische Symptome, diese wurden jedoch nicht weiter beschrieben.{discomfort}{symptoms}',
      unanswered_no:
        'Es liegen keine systemischen Symptome vor.{discomfort}{symptoms}',
      unanswered_unknown:
        'Es ist unklar, ob der Ausschlag systemische Symptome verursacht.{discomfort}{symptoms}',
    },
    rashReliefAndWorseningFactors: {
      yes_yes:
        'Der Ausschlag wird gelindert durch {reliefFactor}, verschlimmert sich jedoch durch {worseningFactor}.',
      yes_unspecified:
        'Der Ausschlag wird gelindert durch {reliefFactor}, verschlimmert sich jedoch durch nicht näher beschriebene Faktoren.{worseningFactor}',
      yes_no:
        'Der Ausschlag wird gelindert durch {reliefFactor}, es wurden keine verschlimmernden Faktoren festgestellt.{worseningFactor}',
      yes_unknown:
        'Der Ausschlag wird gelindert durch {reliefFactor}, aber es ist unklar, ob es verschlimmernde Faktoren gibt.{worseningFactor}',
      yes_unanswered:
        'Der Ausschlag wird gelindert durchh {reliefFactor}. {worseningFactor}',

      unspecified_yes:
        'Es wird von einer Linderung durch unspezisfische Faktoren und einer Verschlechterung durch {worseningFactor}.{reliefFactor} berichtet',
      unspecified_unspecified:
        'Es wird sowohl von Linderung als auch Verschlimmerung berichtet, jedoch ohne Details.{reliefFactor} {worseningFactor}',
      unspecified_no:
        'Es ist unklar, ob es lindernde Faktoren gibt, es wurden aber keine verschlimmernden Faktoren festgestellt.{reliefFactor} {worseningFactor}',
      unspecified_unknown:
        'Von einer Linderung wurde berichtet, aber nicht näher beschrieben. Es ist unklar, ob verschlimmernde Faktoren vorliegen.{reliefFactor} {worseningFactor}',
      unspecified_unanswered:
        'Der Patient erwähnt Linderung, gibt jedoch keine weiteren Details an.{reliefFactor} {worseningFactor}',

      no_yes:
        'Keine bekannten Faktoren lindern den Ausschlag. Verschlimmerung durch {worseningFactor}.{reliefFactor}',
      no_unspecified:
        'Keine bekannten lindernden Faktoren, aber der Ausschlag verschlimmert sich durch nicht näher beschriebene Faktoren.{reliefFactor} {worseningFactor}',
      no_no:
        'Keine bekannten Faktoren lindern oder verschlimmern den Ausschlag.{reliefFactor}{worseningFactor}',
      no_unknown:
        'Keine bekannten lindernden Faktoren und es ist unklar, ob es verschlimmernde Faktoren gibt.{reliefFactor}{worseningFactor}',
      no_unanswered:
        'Keine bekannten lindernden Faktoren.{reliefFactor}{worseningFactor}',

      unknown_yes:
        'Es ist unklar, ob etwas den Ausschlag lindert. Er verschlimmert sich jedoch durch {worseningFactor}. {reliefFactor}',
      unknown_unspecified:
        'Es ist unklar, ob etwas den Ausschlag lindert. Er verschlimmert sich jedoch durch nicht näher beschriebene Einflüsse.{reliefFactor}{worseningFactor}',
      unknown_no:
        'Es ist unklar, ob etwas den Ausschlag lindert, verschlimmernden Faktoren wurden nicht festgestellt.{reliefFactor}{worseningFactor}',
      unknown_unknown:
        'Es ist unklar, ob etwas den Ausschlag lindert oder verschlimmert.{reliefFactor}{worseningFactor}',
      unknown_unanswered:
        'Es ist unklar, ob es lindernde Faktoren gibt.{reliefFactor}{worseningFactor}',

      unanswered_yes:
        'Der Ausschlag verschlimmert sich durch {worseningFactor}.{reliefFactor}',
      unanswered_unspecified:
        'Der Ausschlag verschlimmert sich durch nicht näher beschriebene Faktoren.{reliefFactor}{worseningFactor}',
      unanswered_no:
        'Von keinen verschlimmernden Faktoren berichtet.{reliefFactor}{worseningFactor}',
      unanswered_unknown:
        'Es ist unklar, ob es verschlimmernde Faktoren gibt.{reliefFactor}{worseningFactor}',
    },
  },
  demoPatientDialog: {
    gotIt: 'Verstanden',
    learnMore: 'Mehr erfahren',
    thisIsATest:
      'Dies ist ein <>TEST<>-Patient. Die von Ihnen registrierten Läsionen und/oder Ausschläge werden nicht bewertet. Die eingegebenen Daten werden nach <>24 Stunden<> gelöscht.',
    title: 'Dermloop Demo-Modus',
    youAreEnteringDemoMode:
      'Bitte beachten Sie, dass Sie gleich den <>Dermloop Demo-Modus<> betreten.',
    youAreInDemoMode: 'Sie befinden sich derzeit im <>Dermloop Demo-Modus<>.',
  },
  descriptors: {
    accessExpiresOn: 'Zugang läuft ab am',
    addAdditionalDiagnosis: 'Weitere Diagnose hinzufügen',
    added: 'Hinzugefügt',
    additionalComment: 'Zusätzlicher Kommentar',
    allCorrectness: 'Alle',
    allItemsHasBeenSelected: 'Alle Elemente wurden ausgewählt',
    anItemHasBeenSelected: '{numberOfItemsSelected} Handlung{{en}} ausgewählt',
    annotate: 'Annotieren',
    annotation: 'Annotation',
    annotationRequests: 'Annotierungsanfragen',
    annotations: 'Annotationen',
    areas: 'Bereiche',
    assessedBy: 'Bewertet von',
    assessmentAction: 'Handlung',
    assessmentDifficulty: 'Schwierigkeit',
    biopsyTypes: 'Biopsietypen',
    classicCriteriaForDiagnosis:
      'Klassische Kriterien für Diagnose {diagnosis} ⦁ Mehrfachauswahl möglich',
    clinic: 'Dermloop Klinik',
    clinicalCase: 'Fall',
    clinicalCases: 'Fälle',
    clinicalImage: 'Übersichtsbild',
    clinicalImageDescription: 'Beschreibung des Übersichtsbildes',
    clinicalImageEvaluation: 'Auswertung des Übersichtsbildes',
    clinicalRole: 'Klinische Rolle',
    clinician: 'Behandler',
    closeUpImage: 'Nahaufnahmebild',
    closeUpImageDescription: 'Beschreibung der Nahaufnahme',
    closeUpImageEvaluation: 'Auswertung der Nahaufnahme',
    comment: 'Kommentar',
    contacted: 'Überprüft',
    contactedBy: 'Überprüft von',
    draftCreatedBy: 'Entwurf erstellt von',
    correctness: 'Richtigkeit',
    correctnessDefaultTriggerText: 'Richtigkeit: Alle',
    delete: 'Löschen',
    dateOfReferral: 'Datum der Überweisung',
    dermloop: 'Dermloop',
    dermoscopicCriteria: 'Dermatoskopische Kriterien',
    dermoscopicCriteriaNr: 'Dermatoskopisches Kriterium {index}',
    dermoscopicImage: 'Dermatoskopisches Bild',
    dermoscopicImageDescription: 'Beschreibung des dermatoskopischen Bildes',
    dermoscopicImageEvaluation: 'Auswertung des dermatoskopischen Bildes',
    diagnosedBy: 'Diagnostiziert von',
    diagnoses: 'Diagnosen',
    diagnosis: 'Diagnose',
    diagnosisDifficulty: 'Diagnoseschwierigkeit',
    diagnostics: 'Diagnostik',
    diagnosticsDifficulty: 'Diagnostikschwierigkeit',
    differentialDiagnosis: 'Differentialdiagnose',
    differentialDiagnosisNr: 'Differentialdiagnose {diagnosisNumber}',
    difficulty: 'Schwierigkeit',
    disabled: 'Deaktiviert',
    disposition: 'Disposition',
    editLocation: 'Ort bearbeiten',
    editMedicalRecordLesion: 'Läsion bearbeiten #{lesionId}',
    editMedicalRecordRash: 'Ausschlag bearbeiten #{conditionId}',
    email: 'E-Mail',
    enabled: 'Aktiviert',
    evaluatedBy: 'Ausgewertet von',
    expires: 'Ablaufdatum',
    finalDiagnosis: 'Diagnose',
    formerRatersPlural: 'Vorherig{{nr:er|e}}  Bewerter',
    hasFormerSkinCancer: 'Frühere Hautkrebserkrankung oder malignes Melanom',
    histopathologicalDiagnosis: 'Histopathologische Diagnose',
    histopathologicalEvaluation: 'Histopathologische Auswertung',
    history: 'Verlauf',
    ifNoDiagnosisRashes:
      'Oder wählen Sie unten die Option aus, falls keine Verdachtsdiagnose vorliegt',
    imageNr: '{imageNumber}/{totalImages} Bilder',
    imageQuality: 'Bildqualität',
    imageQualityAssessment: 'Bewertung der Bildqualität',
    images: 'Bilder',
    lesionAge: 'Alter der Läsion',
    lesionEditing: 'Läsion bearbeiten',
    lesionImagePathology: 'Histopathologische Einschätzung',
    lesionImageTeledermatology: 'Dermatologische Einschätzung',
    lesionLocation: 'Läsionslokalisation',
    lesionsPlural: 'Läsion{{nrOfLesions:en}}',
    location: 'Ort',
    medicalUnit: 'Standort',
    medicalUnitInformation: 'Standortinformation',
    medicalUnitRole: 'Standortrolle',
    myCases: 'Meine Fälle',
    myRecentRegisteredLesions: 'Meine letzten eKonsile',
    name: 'Name',
    newInvitation: 'Neue Einladung',
    noDateSelected: 'Kein Datum ausgewählt',
    noDiagnosis: 'Keine Diagnose',
    noFormerRaters: 'Keine früheren Bewerter',
    notAssessedYet: 'Noch nicht bewertet',
    notContacted: 'Nicht überprüft',
    notOnSelectedRecord:
      'Sie befinden sich nicht mehr beim ausgewählten Patienten',
    occupation: 'Beruf',
    orSearchFromMostUsed: 'Oder aus häufig verwendeten auswählen',
    otherObjectiveFindings: 'Weitere objektive Befunde',
    overdueFollowUp: 'Überfällige Verlaufskontrolle',
    overview: 'Übersicht',
    overviewImage: 'Übersichtsbild',
    overviewImageDescription: 'Beschreibung des Übersichtsbildes',
    overviewImageEvaluation: 'Auswertung des Übersichtsbildes',
    pathology: 'Biopsierte Läsionen',
    pathologyID: 'Proben-ID',
    patientContacted: 'Antwort überprüft',
    patientLibrary: 'Patientenkartei durchsuchen',
    personalInformation: 'Persönliche Informationen',
    previousTreatmentOrTrauma: 'Vorherige Behandlung',
    primaryDiagnosis: 'Primäre Diagnose',
    rashesPlural: '{{nrOfRashes:Ausschlag|Ausschläge}}',
    rateClinicalImage: 'Übersichtsbild bewerten',
    rateCloseUpImage: 'Nahaufnahme bewerten',
    rateDermoscopicImage: 'Dermatoskopisches Bild bewerten',
    rateImageQuality: 'Bildqualität bewerten',
    rateOverviewImage: 'Übersichtsbild bewerten',
    raters: 'Bewerter (n=)',
    recommendations: 'Empfehlungen',
    recommendedClinicalAction: 'Empfohlene klinische Handlung',
    referrer: 'Überweiser:in',
    referrersInfo: 'Überweisende Person',
    resultsNr: '{resultNumber} Ergebnis{{se}}',
    resultsNrCapped: '{cap}+ Ergebnisse',
    resultsPlural: 'Ergebnis{{nrOfResults:sse}}',
    reviewedBy: 'Bewertet von',
    searchForDiagnosis: 'Nach Diagnose suchen',
    secondaryDiagnosis: 'Sekundäre Diagnose',
    selectTool: 'Werkzeug auswählen',
    showLess: 'Weniger anzeigen',
    showMore: 'Mehr anzeigen',
    skinTypes: 'Hauttypen',
    specialisation: 'Fachrichtung',
    specialityAndCondition: 'Fachgebiet und Erkrankung',
    status: 'Status',
    stepIndex: 'Schritt 0{stepIndex}',
    tele: 'Eingereichte eKonsile',
    teledermatologicalDiagnosis: 'Diagnose',
    teleDermatologicalDiagnosis: 'Diagnose',
    teleDermatologicalEvaluation: 'Dermatologische Einschätzung',
    teleEvaluation: 'Dermatologische Einschätzung',
    tentativeClinicalDiagnosis: 'Verdachtsdiagnose',
    tertiaryDiagnosis: 'Tertiäre Diagnose',
    type: 'Typ',
    typeACriteria: 'Typ-A-Kriterien',
    unableToDetermine: 'Nicht bestimmbar',
    unitUsers: 'Standortbenutzer',
    userName: 'Benutzername',
    usersPersonalInformation: 'Persönliche Informationen des Benutzers',
    yourPersonalInformation: 'Ihre persönlichen Informationen',
    moreOptions: 'Weitere Optionen',
    edit: 'Bearbeiten',
  },
  deviceLogin: {
    dialog: {
      title: 'Mit dem Dermkit bei Dermloop anmelden',
      description:
        'Melden Sie sich bei Ihrem Dermkit an, indem Sie den QR-Code mit Ihrem Gerät scannen.',
    },
    featureBanner: {
      title: 'Loggen Sie sich mit Ihrem Dermkit per QR-Code in Dermloop ein',
      step1BeforeIcon: 'Klicken Sie auf das Symbol',
      step1AfterIcon: 'oben rechts auf Ihrem Bildschirm.',
      step2:
        'Öffnen Sie die Dermloop App auf Ihrem Dermkit und <>tippen Sie auf die grüne Schaltfläche<> auf der Anmeldeseite:',
      hostedLoginButton: 'Anmelden',
      step3:
        'Scannen Sie den QR-Code auf Ihrem Computerbildschirm mit Ihrem Dermkit. Sie werden dann automatisch angemeldet.',
    },
  },
  diagnosisToolbar: {
    addDiagnosis: 'Weitere Diagnose hinzufügen',
    dermatologyDiagnosis: 'Diagnose von Dermatologe',
    dermatologyDiagnosisNote: 'Notiz',
    diagnosisTypeTitle: 'Welche Art von Diagnose möchten Sie hinzufügen?',
    differentialDiagnosis: 'Differentialdiagnose {count}',
    histoPathologicalDiagnosis: 'Histopathologische Diagnose',
    histopathologyDiagnosisNote: 'Notiz (z. B. Pathologiebefund)',
    notePlaceholder: 'Notiz schreiben...',
    secondaryDiagnosis: 'Sekundäre Diagnose',
    submitButton: 'Speichern',
    tertiaryDiagnosis: 'Tertiäre Diagnose',
    title: 'Diagnose hinzufügen',
  },
  overdueFollowUpToolbar: {
    description:
      'Diese Läsion wurde zur Verlaufskontrolle markiert. Die gewählte Zeit der Verlaufskontrolle ist überschritten, daher ist die Läsion überfällig.',
    stepOne:
      'Um die Markierung „überfällig“ zu entfernen, fügen Sie eine neue klinische Maßnahme für diese Läsion hinzu.',
    stepTwo: 'Klicken Sie dazu auf',
    editOrTakeAction: '"Bearbeiten".',
  },
  displayNameYou: '{lastName}, {firstName} {youString|{(you): (Sie), *:}}',
  dropdownItems: {
    changeMedicalUnit: 'Standort wechseln',
    logout: 'Abmelden',
    profile: 'Profil',
    settings: 'Einstellungen',
    unitUsers: 'Standortbenutzer',
    smartPhrases: 'Textbausteine',
  },
  errorMessages: {
    alreadyInvitedToMedicalUnit:
      'Der Benutzer wurde bereits zum Standort eingeladen',
    duplicateSorId:
      'Ein klinischer Standort mit dieser SOR-ID existiert bereits',
    errorOccurred: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
    errorOccurredUpdatingLesion:
      'Beim Aktualisieren der Läsion ist ein Fehler aufgetreten',
    errorOccurredWhileLoadingMedicalRecord:
      'Beim Laden der Patientenakte ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    errorOccurredWhileSaving: 'Beim Speichern ist ein Fehler aufgetreten',
    conditionNotFound:
      'Der Befund konnte nicht gefunden werden. Möglicherweise wurde er gelöscht.',
    errorWhileCreatingAssessment:
      'Fehler beim Erstellen der dermatologsichen Einschätzung',
    inputCorrectInputPlease: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
    invalidCPR: 'Ungültige CPR',
    lesionNotInHealthRecord: 'Läsion stimmt nicht mit Patientendaten überein',
    pleaseFillOutAllFields: 'Bitte füllen Sie alle Felder aus',
    thisFieldIsRequired: 'Dieses Feld ist erforderlich',
    uIdNotFound:
      '{UID|{SSN:CPR,MRN:Patienten-Nr.}} stimmt mit keinem Datensatz überein',
    youAreNotAuthorizedInAnyMedicalUnits:
      'Hallo {name}, Sie sind für keinen Standort autorisiert.',
    youAreNotPermittedToAccessThisPatient:
      'Sie dürfen auf diesen Patienten nicht zugreifen',
  },
  fieldPlaceholders: {
    cpr: 'Nach CPR suchen',
    cprOrPathologyID: 'Nach CPR oder Proben-ID suchen',
    cprOrSpecimentID: 'Nach CPR oder Proben-ID suchen',
    enterCommentHere: 'Geben Sie hier Ihren Kommentar ein...',
    loading: 'Lädt...',
    mrn: 'Nach Patienten-Nr. suchen',
    mrnOrName: 'Nach Patienten-Nr. oder Name suchen',
    cprOrName: 'Nach CPR oder Name suchen',
    otherClinicalAction: 'Andere Handlunghier eingeben...',
    pathologyId: 'Nach Proben-ID suchen',
    searchDiagnosis: 'Nach Diagnose suchen',
    searchForCriteria: 'Nach Kriterium suchen',
    searchLocation: 'Nach Ort suchen',
    selectMedicalUnit: 'Standort auswählen',
    selectOccupation: 'Beruf auswählen',
    selectSpecialisation: 'Fachrichtung auswählen',
    typeHereToEnterDescription: 'Geben Sie hier eine Beschreibung ein...',
  },
  filters: {
    skinType: 'Hauttyp',
    resetFilter: 'Filter zurücksetzen',
    jurisdictionalGroup: {
      title: 'Netzwerk',
      searchText: 'Nach Netzwerk suchen',
      searchPlaceholder: 'Netzwerk eingeben',
      listTitle: 'Netzwerke',
      selectedListTitle: 'Ausgewählte Netzwerke',
      noResultsText: 'Keine Netzwerke gefunden',
    },
    medicalUnit: {
      title: 'Standorte',
      searchText: 'Nach Standort suchen',
      searchPlaceholder: 'Standortnamen eingeben',
      listTitle: 'Standorte',
      selectedListTitle: 'Ausgewählte Standorte',
      noResultsText: 'Keine Standorte gefunden',
    },
    dateOfReferral: {
      title: 'Überweisungsdatum',
    },
    diagnoses: {
      availableDiagnosesText: 'Alle Diagnosen',
      clearText: 'Diagnosen löschen',
      diagnosisSearchText: 'Nach Diagnose suchen',
      emptyText: 'Keine Diagnosen gefunden',
      placeholder: 'Diagnose eingeben',
      selectedDiagnosisText: 'Ausgewählte Diagnosen',
      title: 'Diagnose',
      withTabs: {
        clinicalTabText: 'Verdachtsdiagnose',
        finalTabText: 'Diagnose',
        histopathologicalTabText: 'Histopathologische Diagnose',
        teleTabText: 'eKonsil Diagnose',
        tentativeTabText: 'Verdachtsdiagnose',
      },
    },
    actions: {
      title: 'Empfohlene Handlung',
      clearText: 'Handlungen löschen',
      availableActionsText: 'Alle Handlungen',
      selectedActionsText: 'Ausgewählte Handlungen',
      placeholder: 'Handlung',
      searchText: 'Nach Handlung suchen',
    },
    difficulty: {
      title: 'Schwierigkeit',
    },
    location: {
      availableLocationText: 'Alle Orte',
      clearText: 'Orte löschen',
      emptyText: 'Keine Orte gefunden',
      locationSearchText: 'Nach Ort suchen',
      placeholder: 'Ort eingeben',
      selectedLocationText: 'Ausgewählte Orte',
      title: 'Ort',
    },
    referralCreatedAfter: {
      title: 'eKonsil erstellt nach',
    },
    referralCreatedBefore: {
      title: 'eKonsil erstellt vor',
    },
    search: {
      insuranceInfo: 'Nach Patientenname oder Patienten-Nr. suchen',
    },
    status: {
      all: 'Alle',
      annotated: 'Annotiert',
      approved: 'Genehmigt',
      assessed: 'Bewertet',
      canceled: 'Storniert',
      closed: 'Abgeschlossen',
      newReferral: 'Neues eKonsil',
      pendingInformation: 'Ausstehende Informationen',
      pending: 'Ausstehend',
      released: 'Freigegeben',
      skipped: 'Übersprungen',
      title: 'Status',
    },
    marked: {
      title: 'Lesezeichen',
      all: 'Alle eKonsile',
      marked: 'Markierte eKonsile',
    },
    multiSelect: {
      availableStatus: 'Verfügbar',
      clearText: 'Zurücksetzen',
      selectedStatus: 'Ausgewählt',
      triggerText: 'Status',
    },
  },
  genders: {
    female: 'weiblich',
    male: 'männlich',
  },
  recentRegistrations: {
    title: 'Meine Registrierungen',
    empty: 'Keine kürzlich erfassten Registrierungen',
  },
  headerElements: {
    annotations: 'Annotationen',
    biopsies: 'Biopsien',
    cases: 'Fälle',
    home: 'Startseite',
    announcementBanner: {
      dismiss: 'Ausblenden',
      minimise: 'Minimieren',
      expandCritical: '1 kritische Mitteilung — aufklappen',
    },
    demoModeHeader: {
      dermloopDemoMode: 'Dermloop Demo-Modus',
    },
    statistics: 'Statistiken',
    teleReferrals: 'eKonsile',
  },
  informationFieldLabels: {
    email: 'E-Mail',
    firstName: 'Vorname',
    lastName: 'Nachname',
    phoneNumber: 'Telefonnummer',
  },
  administrationReferralsTable: {
    title: 'Unbeantwortete eKonsile',
    noEConsults: 'Keine unbeantworteten eKonsile in dieser Föderation',
    columns: {
      eConsultNumber: 'eKonsil-Nr.',
      created: 'Erstellt',
      medicalUnit: 'Einheit',
      type: 'Typ',
      draft: 'Entwurf',
      released: 'Freigegeben',
      releasedDate: 'Freigabedatum',
      releasedToAllowed: 'An zugelassene Mitglieder freigegeben',
      status: 'Status',
      assignment: 'Zuweisung',
    },
    values: {
      tele: 'Tele',
      physical: 'Physisch',
      unassigned: 'Nicht zugewiesen',
    },
    statuses: {
      open: 'Offen',
      pendingInformation: 'Informationen ausstehend',
      closed: 'Geschlossen',
      cancelled: 'Storniert',
    },
    filters: {
      available: 'Verfügbar',
      selected: 'Ausgewählt',
      newestFirst: 'Neueste zuerst',
      oldestFirst: 'Älteste zuerst',
      sortCreated: 'Nach Erstellung sortieren',
    },
    actions: {
      moreOptions: 'Weitere Optionen',
      viewCase: 'Fall anzeigen',
      changeStatus: 'Status ändern',
      release: 'Freigeben',
    },
    toast: {
      statusUpdated: 'Status aktualisiert',
      statusUpdateFailed: 'Status konnte nicht aktualisiert werden',
      released: 'eKonsil freigegeben',
      releaseFailed: 'eKonsil konnte nicht freigegeben werden',
    },
  },
  insuranceInfoTable: {
    accNr: 'Account-Nr.',
    assessed: 'Bewertet',
    assign: 'Zuweisen',
    assignEConsultTo: 'eKonsile zuweisen an...',
    assignTo: 'Zuweisen an...',
    cancelEConsult: 'eKonsile stornieren',
    cancelled: 'Storniert',
    closed: 'Abgeschlossen',
    pendingInformation: 'Ausstehende Informationen',
    closeEConsult: 'eKonsile abschließen',
    dob: 'Geburtsdatum',
    eConsultId: 'eKonsil-ID',
    editInsuranceInfoToolbar: {
      assignedTo: 'Zugewiesen an',
      changeStatus: 'Status ändern',
      dateFormat: 'Datumsformat',
      dob: 'Geburtsdatum',
      downloadPDF: 'PDF herunterladen',
      resendFax: 'Fax erneut senden',
      firstName: 'Vorname',
      icd10Code: 'ICD10-Code',
      insuranceNumber: 'Versicherungsnummer',
      insurer: 'Versicherung',
      lastName: 'Nachname',
      mrn: 'Patienten-Nr.',
      patientAddress: 'Patientenadresse',
      patientCity: 'Wohnort',
      patientState: 'Bundesland',
      patientZip: 'PLZ',
      payer: 'Kostenträger',
      placeholders: {
        dob: 'Geburtsdatum des Patienten',
        firstName: 'Vorname des Patienten',
        icd10Code: 'ICD10-Code',
        insuranceNumber: 'Versicherungsnummer des Patienten',
        insurer: 'Name der Versicherung eingeben',
        lastName: 'Nachname des Patienten',
        mrn: 'Patienten-Nr.',
        patientAddress: 'Adresse des Patienten',
        patientCity: 'Stadt des Patienten',
        patientState: 'Bundesland',
        patientZip: 'PLZ',
        payer: 'Wer übernimmt die Kosten?',
        question: 'Warum benötigt der Patient ein eKonsil?',
      },
      question: 'Klinische Fragestellung',
      save: 'Speichern',
      saveAndRelease: 'Speichern und freigeben',
      title: 'Versicherungsdaten bearbeiten',
      validationMessages: {
        icd10Code: 'ICD10-Code ist erforderlich',
        mrn: 'Patienten-Nr. ist erforderlich',
        name: 'Name ist erforderlich',
        patientAddress: 'Adresse ist erforderlich',
        patientCity: 'Stadt ist erforderlich',
        patientState: 'Bundesland ist erforderlich',
        patientZip: 'Postleitzahl ist erforderlich',
        payer: 'Kostenträger ist erforderlich',
        question: 'Klinische Fragestellung ist erforderlich',
      },
    },
    superSeededText: 'eKonsil ersetzt',
    superSeededTooltip:
      'Neues eKonsil mit zusätzlicher Handlung eingereicht. Bitte prüfen Sie dies.',
    exportCSV: 'CSV exportieren',
    from: 'Von',
    moreOptions: 'Weitere Optionen',
    network: 'Netzwerk',
    newReferral: 'Neu',
    noEConsults: 'Kein eKonsil gefunden',
    patient: 'Patient',
    referral: 'eKonsil',
    released: 'Freigegeben',
    searchSpecialistPlaceholder: 'Nach Dermatologen suchen',
    specialist: 'Dermatologe',
    status: 'Status',
    suggestedSpecialist: 'Vorgeschlagener Dermatologe',
    title: 'eKonsile',
    toast: {
      errorSaving: 'Fehler beim Speichern der Versicherungsdaten',
      errorCanceled: 'Fehler beim Stornieren des eKonsils',
      errorClosing: 'Fehler beim Abschließen des eKonsils',
      errorExportCSV: 'CSV-Export fehlgeschlagen',
      errorOnReassignSpecialist:
        'Fehler beim Zuweisen des eKonsils an einen anderen Dermatologen',
      errorUnCanceling: 'Fehler beim Rückgängigmachen der Stornierung',
      successfullyCanceled: 'eKonsil erfolgreich storniert',
      successfullyMarkedPendingInformation:
        'eKonsil als ausstehende Information markiert',
      errorMarkingPendingInformation:
        'Fehler beim Markieren des eKonsils als ausstehende Information',
      successfullyClosed: 'eKonsil erfolgreich abgeschlossen',
      successfullyExportedCSV: 'CSV erfolgreich heruntergeladen',
      successfullyReassignedSpecialist:
        'eKonsil erfolgreich einem anderen Dermatologen zugewiesen',
      successfullyUnCanceled:
        'Stornierung des eKonsils erfolgreich rückgängig gemacht',
      successfullySavedInsuranceInfo:
        'Versicherungsdaten erfolgreich gespeichert',
    },
    unCancelEConsult: 'Stornierung des eKonsils rückgängig machen',
    viewCaseDetails: 'Falldetails anzeigen',
  },
  invitationExpired: {
    title: 'Dermloop Desktop',
    subtitle: 'Einladung abgelaufen',
    messageWithEmail: 'Die Einladung an (<>{email}<>) ist abgelaufen.',
    messageWithoutEmail: 'Die Einladung ist abgelaufen.',
    contactSuperuser:
      'Bitte wenden Sie sich an Ihren Superuser, wenn Sie Zugang zu Dermloop benötigen.',
  },
  macroscopicInspectionType:
    '{type|{None: Keine, Assymetrical: Asymmetrisch, Irregular Border: Unregelmäßiger Rand, Several Colours: Mehrere Farben, Diameter >6 mm: Durchmesser >6 mm, Other: Sonstiges}}',
  medicalRecordValues: {
    age: '(Alter: {age})',
    appearanceChanges: '{triageInfo}',
    none: 'Keine',
    notRegistered: 'Nicht registriert',
  },
  clinicalAssessmentToolbar: {
    missingImagesTitle: 'Fehlende Bilder!',
    missingImagesDescription:
      'Die benötigten Bilder sind in diesem Fall nicht vorhanden. Bitte erstellen Sie Aufnahmen mit der mobilen App.',
  },
  pathologyAssessmentToolbar: {
    createAssessmentSuccess: 'Pathologiebefundung erfolgreich erstellt',
    primaryDiagnosisErrorMessage: 'Primäre Diagnose ist erforderlich',
    diagnosisDifficultyErrorMessage: 'Schwierigkeitsgrad ist erforderlich',
    breslowThicknessErrorMessage:
      'Gültige Zahl mit maximal einer Dezimalstelle eingeben',
    breslowThicknessPlaceholder: 'Breslow-Dicke hier eingeben...',
    breslowThicknessTitle: 'Breslow-Dicke (mm)',
    breslowThicknessUnknown: 'Tumordicke unbekannt (Tx)',
    referredBy: 'eKonsil gesendet von',
  },
  patientPage: {
    markPatient: {
      successfullyBookmarkedCondition: 'eKonsile markiert',
      successfullyRemovedBookmark: 'Markierung entfernt',
      mark: 'Markieren',
      marked: 'Markiert',
    },
    alert: {
      cancel: 'Abbrechen',
      pleaseNoteNotContactedPatient:
        'Bitte beachten Sie, dass der Patient derzeit NICHT als kontaktiert markiert ist.',
      takeAction: 'Handlung ergreifen',
      takeNewClinicalAction: 'Neue klinische Handlung durchführen?',
      weAssumePatientIsContacted:
        'Wenn Sie eine neue Handlung auswählen, gehen wir davon aus, dass Sie den Patienten kontaktiert haben.',
    },
    jumpToToolbar: {
      completeDraft: 'Entwurf abschließen',
      saveAssessment: 'Beurteilung speichern',
      addDiagnosis: 'Diagnose hinzufügen',
      completeEConsult: 'eKonsil abschließen',
      completePathology: 'Pathologie abschließen',
      reviewEConsult: 'eKonsil prüfen',
      reviewPathology: 'Pathologie prüfen',
      completeFollowUp: 'Nachkontrolle abschließen',
      clinicalCase: 'Klinischer Fall',
    },
    editContact: {
      showAll: 'Telefonnummer, E-Mail und Adresse anzeigen',
      hideAll: 'Telefonnummer, E-Mail und Adresse verbergen',
      showPhone: 'Telefonnummer anzeigen',
      hidePhone: 'Telefonnummer ausblenden',
      showAddress: 'E-Mail und Adresse anzeigen',
      hideAddress: 'E-Mail und Adresse verbergen',
      missingInfo: 'Fehlende Kontaktdaten',
      addPhone: 'Hier klicken, um Telefonnummer hinzuzufügen',
      addEmail: 'Hier klicken, um E-Mail hinzuzufügen',
      addAddress: 'Hier klicken, um Adresse hinzuzufügen',
      editPhone: 'Telefonnummer bearbeiten',
      editEmail: 'E-Mail bearbeiten',
      editAddress: 'Adresse bearbeiten',
      phoneNumber: 'Telefonnummer',
      address: 'Adresse',
      city: 'Stadt',
      postalCode: 'Postleitzahl',
      email: 'E-Mail',
      invalidEmail: 'Geben Sie eine gültige E-Mail-Adresse ein',
      invalidPostalCode: 'Geben Sie eine gültige Postleitzahl ein',
      confirm: 'Bestätigen',
      discard: 'Verwerfen',
      toastSuccess: 'Kontaktdaten aktualisiert',
      toastError: 'Kontaktdaten konnten nicht aktualisiert werden',
    },
  },
  plaintext: {
    additionalMedicalUnitRoles: 'Zusätzliche Standortrolle{{nrOfRoles:n}}',
    all: 'Alle',
    contact: 'Kontakt',
    copiedFromDermloop: 'Kopiert von Dermloop',
    inviteByEmail: 'Per E-Mail einladen',
    linkToCondition: 'Link zum Befund',
    melatechsTeam: 'Melatech-Team',
    on: 'am',
    onceYouRemove: 'Sobald Sie entfernen,',
    restoreAccessByInvitingAgain:
      'Sie können den Zugang wiederherstellen, indem Sie den Benutzer erneut einladen.',
    roleToThisUser: 'Rollen für diesen Benutzer.',
    theyWillNoLongerHaveAccessTo: 'sie haben dann keinen Zugriff mehr auf',
    thisInvitationExpires: 'Diese Einladung läuft ab am {date}',
    thisMedicalUnit: 'diesen Standort.',
    toAddAdditional: 'um weitere hinzuzufügen',
    toRemoveThisUser: 'um diesen Benutzer zu entfernen',
    useDermloopMobileApp:
      'Nutzen Sie Dermloop Capture, um neue Bilder zu erfassen',
    yearOld: 'Jahre alt',
    youHaveBeenInvitedTo: 'Sie wurden eingeladen zu',
  },
  referralStatisticsPage: {
    dailyStatisticsMissing: 'Keine Tagesstatistiken verfügbar',
    dailyStatisticsTitle: 'Tagesstatistiken',
    dailyStatisticsToDateLabel: 'Zwei Wochen vorher...',
    jurisdictionalGroupNameColumn: 'Zuständigkeitsgruppe',
    pending24HoursColumn: '24–48 Stunden',
    pending48HoursColumn: '48–96 Stunden',
    pending96HoursColumn: '>96 Stunden',
    title: 'Überweisungsstatistiken',
    totalPendingColumn: 'Insgesamt ausstehend',
    totalPendingReferrals24HoursTitle: '24–48 Stunden',
    totalPendingReferrals24HoursTooltip:
      'Überweisungen, die vor mehr als 24, aber weniger als 48 Stunden erstellt wurden',
    totalPendingReferrals48HoursTitle: '48–96 Stunden',
    totalPendingReferrals48HoursTooltip:
      'Überweisungen, die vor mehr als 48, aber weniger als 96 Stunden erstellt wurden',
    totalPendingReferrals96HoursTitle: '>96 Stunden',
    totalPendingReferrals96HoursTooltip:
      'Überweisungen, die vor mehr als 96 Stunden erstellt wurden',
    totalPendingReferralsTitle: 'Insgesamt ausstehende Überweisungen',
    totalPendingReferralsTooltip: 'Gesamtanzahl ausstehender Überweisungen',
    valueCouldNotBeLoaded: 'Wert konnte nicht geladen werden',
  },

  seranoteButton: {
    new: 'Neues',
  },
  searchResults: {
    adjustMessage:
      'Versuchen Sie, Ihre Suche oder Filtereinstellungen anzupassen',
    noResults: 'Keine Ergebnisse gefunden',
  },
  smartPhrases: {
    diagnosisNotFound: 'Diagnose nicht gefunden...',
    actionNotFound: 'Handlung nicht gefunden...',
    teleDialog: {
      updateHeader: 'Bestehenden Textbaustein aktualisieren?',
      updateContent:
        'Dies ersetzt den bestehenden Textbaustein für {diagnosis} und {recommendedAction}.',
      diagnosisAndAction: '{diagnosis} und {recommendedAction}.',
      currentSmartPhrase: 'Aktueller Textbaustein:',
      cancelButton: 'Abbrechen',
      updateButton: 'Aktualisieren',
    },
    changeAlert: {
      title: 'Aktuellen Text durch Textbaustein ersetzen?',
      content:
        'Sie haben den Textbaustein geändert. Möchten Sie den aktuellen Text behalten oder ihn durch den neuen Textbaustein ersetzen?',
      keepButton: 'Aktuellen Text behalten',
      replaceButton: 'Mit Textbaustein ersetzen',
    },
    errorMessages: {
      phraseRequired: 'Textbaustein ist erforderlich',
      actionRequired: 'Empfohlene Handlung ist erforderlich',
      diagnosisRequired: 'Diagnose ist erforderlich',
    },
    toast: {
      successfullyUpdatedSmartPhraseOption:
        'Textbausteine wurden erfolgreich {state|{enabled:aktiviert, disabled:deaktiviert}}',
      errorUpdatingSmartPhrasesOption:
        'Ein Fehler ist aufgetreten. Ihre Textbaustein-Einstellungen konnten nicht aktualisiert werden',
      successfullyDeleted: 'Textbaustein erfolgreich gelöscht',
      success: 'Textbaustein erfolgreich erstellt.',
      updated: 'Textbaustein erfolgreich aktualisiert',
      error: 'Fehler beim Erstellen des Textbausteins.',
      errorDeletion: 'Fehler beim Löschen des Textbausteins',
      noSmartPhraseId:
        'Fehler beim Aktualisieren. Keine Textbaustein-ID gefunden.',
    },
    smartPhrases: 'Textbausteine',
    results: 'Ergebnis{{nrOfImages:sse}}',
    subTitle:
      'Dieser Textbaustein erscheinen automatisch im Kommentarfeld, sobald Sie eine Diagnose und eine Handlung für einen Fall auswählen.',
    toolbar: {
      diagnosisPickHeader: 'Diagnose und empfohlene Handlung wählen',
      diagnosisAndActionHeader: 'Diagnose und empfohlene Handlung',
      diagnosisStep: 'Diagnose',
      enterDiagnosis: 'Diagnose eingeben',
      enterSmartPhrase: 'Ihren Textbaustein hier eingeben...',
      addSmartPhrase: 'Textbaustein hinzufügen',
      recommendedAction: 'Empfohlene Handlung',
      editSmartPhrase: 'Textbaustein bearbeiten',
      saveChanges: 'Änderungen speichern',
      insertSmartPhrase: 'Textbaustein einfügen',
      clearNote: 'Notiz löschen',
      updateSmartPhrase: 'Textbaustein aktualisieren',
      smartPhraseSaved: 'Textbaustein gespeichert',
      createSmartPhrase: 'Textbaustein erstellen',
      enterNoteHere: 'Notiz hier eingeben...',
      smartPhrase: 'Textbaustein',
    },
    table: {
      delete: 'Löschen',
      cancel: 'Abbrechen',
      deleteSmartPhrase: 'Textbaustein löschen?',
      cantBeUndone: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      diagnosis: 'Diagnose',
      recommendedAction: 'Empfohlene Handlung',
      smartPhrase: 'Textbaustein',
      noSmartPhrases: 'Sie haben noch keinen Textbaustein.',
      createSmartPhrase: 'Textbaustein erstellen',
      searchPlaceholder: 'Nach Textbaustein suchen',
      smartPhrasesEnabled: 'Textbaustein aktiviert',
    },
    tooltips: {
      addSmartPhrase:
        'Um einen Testbaustein zu erstellen, müssen sowohl eine Diagnose als auch eine Empfehlung ausgewählt werden.',
      actionAlreadyExists:
        'Für die gewählte Kombination existiert bereits ein Textbaustein.',
    },
  },
  conditionHistory: {
    actions: {
      addedNewDiagnosis: 'Neue Diagnose',
    },
    title: {
      newDiagnosis:
        'Neue {diagnosisType|{pathologicalDiagnosis:histopathologische, dermatologicalDiagnosis:dermatologische, *:}} Diagnose hinzugefügt',
      pathology: 'Histopathologische Auswertung',
      patientContacted: 'Antwort überprüft',
      conditionDetails: '{type}-Informationen geändert',
      teledermatology: 'Dermatologische Einschätzung',
    },
  },
  conditionList: {
    testPatient: 'Testpatient',
  },
  conditionOverview: {
    addNewDiagnosis: 'Neue Diagnose hinzufügen',
    sendToJournal: 'An Patientenakte senden',
    sendToJournalSuccess: 'Patientenakte erfolgreich gesendet',
    sendToJournalError:
      'Fehler beim Senden an die Patientenakte. Bitte erneut versuchen',
  },
  conditionPage: {
    backToPatientOverview: 'Zurück zur Patientenübersicht',
    collapseSidebar: 'Seitenleiste einklappen',
    patientSectionLabel: 'Patient',
    specialitySectionLabel: 'Fachgebiet',
    locationSectionLabel: 'Lokalisation',
    draftBadge: 'Entwurf',
    editCase: 'Fall bearbeiten',
    copyJournal: 'Journal kopieren',
    more: 'Mehr',
    downloadAllPhotos: 'Alle Bilder herunterladen',
    downloadingPhotos: 'Bilder werden heruntergeladen...',
    imagesPlaceholder: 'Bilder',
    medicalJournal: 'Journal',
    assessment: 'Beurteilung',
    taskCompleted: 'Aufgabe abgeschlossen',
    taskCompletedNext: 'Weiter zur nächsten Aufgabe.',
  },
  conditionResponse: {
    title: 'Facharztantwort',
    sent: 'Antwort gesendet',
    sentToast:
      'Ihre Antwort wurde an die überweisende Ärztin/den überweisenden Arzt gesendet',
    couldNotOpen:
      'Das Antwortformular konnte nicht geöffnet werden. Bitte versuchen Sie es erneut.',
    noAssignedForm:
      'Für diesen Befundtyp ist kein Antwortformular eingerichtet, daher kann diese Überweisung hier nicht beantwortet werden. Bitte wenden Sie sich an den Support.',
  },
  conditionResponseReview: {
    title: 'Antwort prüfen',
    send: 'Als gelesen markieren',
    sentToast: 'Fall als geprüft markiert',
    reviewedBy: 'Gelesen von {name}',
    ratingTooltip:
      'Diese Bewertung wird erhoben, um sicherzustellen, dass die Antworten der Fachärzte stets von hohem Standard sind. Die Bewertung ist für den Facharzt nicht sichtbar.',
  },
  moveConditionDialog: {
    title: 'Befund verschieben',
    description:
      'Diesen Befund mit allem, was dazugehört, zu einer anderen Patientin oder einem anderen Patienten derselben Zuständigkeitsgruppe verschieben. Die Zielperson behält ihre eigene Identität.',
    whatMoves: 'Was verschoben wird',
    conditionLabel: 'Befund',
    currentPatient: 'Aktuelle Patientin / aktueller Patient',
    destinationPatient: 'Zielpatientin / Zielpatient',
    unnamedPatient: 'Ohne Namen',
    triages: 'Erfassungen ({count})',
    detailsNoteTitle: 'Wird mit dem Befund verschoben',
    detailsNote:
      'Befunddetails, Diagnosen, Markierungen und die Bilder dieser Erfassungen folgen dem Befund automatisch.',
    createNewPatient: 'Neue Patientin / neuen Patienten anlegen',
    createPatientTitle: 'Zielperson anlegen',
    createPatientFromLookup:
      'Die Angaben stammen aus dem Melderegister und werden so gespeichert.',
    registerNotFound:
      'Das Melderegister kennt keine Person mit dieser Kennung, daher kann der Fall nicht dorthin verschoben werden.',
    registerDead:
      'Das Melderegister führt diese Person als verstorben, daher kann der Fall nicht dorthin verschoben werden.',
    registerFailed:
      'Das Melderegister war nicht erreichbar. Gehen Sie zurück und versuchen Sie es erneut.',
    createPatientManually:
      'Hier ist kein Registerabruf verfügbar — geben Sie die Angaben der Zielperson ein.',
    lookingUp: 'Kennung wird abgefragt...',
    selectGender: 'Geschlecht wählen',
    createPatientNoteTitle: 'Hier wird nur die Identität erfasst',
    createPatientNote:
      'Name, Kennung, Geburtsdatum und Geschlecht. Kontaktdaten und Einwilligungen werden nicht übernommen.',
    findPatient: 'Zielperson finden',
    search: 'Suchen',
    noPatientFound:
      'In dieser Zuständigkeitsgruppe gibt es niemanden mit dieser Kennung.',
    samePatient: 'Der Befund gehört bereits zu dieser Person.',
    searchFailed:
      'Die Suche ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
    fields: {
      firstName: 'Vorname',
      lastName: 'Nachname',
      birthDate: 'Geburtsdatum',
      gender: 'Geschlecht',
    },
    confirmTitle: 'Verschieben bestätigen',
    confirmDescription:
      'Der Befund, seine Erfassungen, Details und Bilder gehören danach zur Zielperson. Dies kann in der App nicht rückgängig gemacht werden.',
    confirmRenameDescription:
      'Dies ist der einzige Fall der aktuellen Person, daher wird keine neue Person angelegt. Die aktuelle Person erhält die hier gezeigte Identität, und Kontaktdaten und Einwilligungen werden entfernt. Dies kann in der App nicht rückgängig gemacht werden.',
    sourceDeletedTitle: 'Die aktuelle Person wird gelöscht',
    sourceDeletedNote:
      'Sie hat keine weiteren Fälle. Name, Kennung, Kontaktdaten und Einwilligungen werden entfernt.',
    next: 'Weiter',
    back: 'Zurück',
    cancel: 'Abbrechen',
    move: 'Befund verschieben',
    moving: 'Wird verschoben...',
    toastMessages: {
      moveSuccess: 'Befund verschoben',
      moveFailed: 'Befund konnte nicht verschoben werden: {error}',
    },
  },
  conditionForm: {
    notAnswered: 'Nicht beantwortet',
    mostUsedDiagnoses: 'Häufig verwendet',
    noSubmission: 'Keine Formularantwort für diesen Befund',
    expandImage: '{label} im Vollbild öffnen',
    noPhotoCaptured: 'Es wurde kein {label}-Bild aufgenommen',
    previousImage: 'Vorheriges Bild',
    nextImage: 'Nächstes Bild',
    nImages: '{count} Bild{{count:er}}',
    previousThumbnails: 'Vorherige Miniaturbilder',
    nextThumbnails: 'Nächste Miniaturbilder',
    closeImage: 'Bild schließen',
    downloadImage: 'Bild herunterladen',
    showFlashImage: 'Mit Blitz anzeigen',
    hideFlashImage: 'Ohne Blitz anzeigen',
    showNoFlashImage: 'Ohne Blitz anzeigen',
    hideNoFlashImage: 'Mit Blitz anzeigen',
    showImageMarker: 'Markierung anzeigen',
    hideImageMarker: 'Markierung ausblenden',
    compare: 'Vergleichen',
    compareImages: 'Bilder vergleichen',
    imageActionUnavailable: 'Noch nicht verfügbar',
    unsupportedWidget:
      'Dieses Formular enthält eine Frage, die diese Version nicht anzeigen kann.',
    unsupportedLocationMap:
      'Diese Version kann die vom Formular verwendete Anatomieansicht nicht anzeigen.',
    notePlaceholder: 'Weitere Details hier angeben...',
    placeMarkerHint:
      'Klicken Sie auf die Illustration, um die Markierung zu setzen',
    editLocation: 'Lokalisation bearbeiten',
    saving: 'Wird gespeichert...',
    savedAt: 'Gespeichert {time}',
    saveFailed: 'Änderungen nicht gespeichert',
    completeRegistration: 'Registrierung abschließen',
    conclude: 'Abschließen',
    discardDraft: 'Entwurf verwerfen',
    submissionIncomplete: 'Einige Pflichtfragen sind noch unbeantwortet.',
    imagesNeedCapture:
      'Ein erforderliches Foto fehlt. Fotos können nur in Capture hinzugefügt werden.',
    staleRevision:
      'Jemand anderes hat den Fall gespeichert, nachdem Sie mit der Bearbeitung begonnen haben. Laden Sie neu, um die neueste Version zu bearbeiten.',
    reload: 'Neu laden',
    changesSaved: 'Änderungen gespeichert',
    awaitingAnswer: 'Antwort ausstehend',
    answered: 'Beantwortet',
    teleReferral: 'Telekonsil',
    saveChanges: 'Änderungen speichern',
    saveAndSendTeleReferral: 'Speichern und Telekonsil senden',
    saveAndRecordActions: 'Speichern und {count} Maßnahme{{count:n}} erfassen',
    actionIncomplete:
      'Vervollständigen Sie die Angaben jeder neuen Maßnahme oder entfernen Sie sie.',
    clinicalActions: 'Klinische Maßnahmen',
    cancelled: 'Storniert',
    closed: 'Geschlossen',
    recorded: 'Erfasst',
    cancelReferral: 'Überweisung stornieren',
    keepReferral: 'Überweisung behalten',
    newClinicalAction: 'Neue klinische Maßnahme',
    noNewAction: 'Keine neue Maßnahme',
    anotherAction: 'Noch einmal {action}',
    actionLimitTeleReferral:
      'Ein Telekonsil wartet bereits auf eine Antwort. Der Facharzt sieht diese Änderungen.',
    actionLimitDismissal: 'Die letzte Maßnahme schließt den Fall bereits ab.',
    actionLimitMixed: 'Ein Speichern erfasst nur eine Art von Maßnahme.',
    cancelReferralTitle: 'Dieses Telekonsil stornieren?',
    cancelReferralDescription:
      'Der Facharzt sieht es dann nicht mehr. Es wird beim Speichern storniert, bis dahin können Sie es rückgängig machen.',
    undo: 'Rückgängig',
    removeExistingAction: '{label} entfernen',
    dropQueuedAction: '{label} verwerfen',
    saveAndCancelTeleReferral: 'Speichern und Telekonsil stornieren',
    saveAndRemoveActions: 'Speichern und {count} Maßnahme{{count:n}} entfernen',
    saveAndApplyChanges:
      'Speichern und {count} Änderung{{count:en}} übernehmen',
    actionRequired:
      'Fügen Sie eine Maßnahme hinzu, um die Registrierung abzuschließen.',
    encounterRequired:
      'Öffnen Sie den Fall aus Epic, um die Registrierung abzuschließen.',
    actionsNotAllowed:
      'Diese Maßnahmen können nicht zusammen erfasst werden. Machen Sie eine der Änderungen rückgängig.',
    actionSent: 'Gesendet am {date}',
    actionSentBy: 'Gesendet am {date} von {name}',
    actionTakenBy: '{date} von {name}',
    removedOnSave: 'Wird beim Speichern entfernt',
    cancelledOnSave: 'Wird beim Speichern storniert',
  },
  patientDeviceLogin: {
    open: 'In Dermkit öffnen',
    close: 'QR-Code schließen',
    instruction:
      'Öffnen Sie Dermkit auf Ihrem Smartphone und scannen Sie den Code.',
    autoExpiry: 'Der Code läuft nach dem Scannen automatisch ab.',
    remaining: '{seconds} Sekunden verbleibend',
    loading: 'QR-Code wird erstellt',
    error: 'Der QR-Code konnte nicht erstellt werden.',
    retry: 'Erneut versuchen',
  },
  tableColumns: {
    latestActivityOn: 'Letzte Aktivität:',
    activityBy: 'von',
    referredOn: 'Überwiesen am:',
    tentativeShort: 'V:',
    finalShort: 'E:',
    referred: 'Überwiesen',
    on: 'Am:',
    by: 'Von:',
    latestActionAndStatus: 'Letzte Aktion und Status',
    clinical: 'Klinisch:',
    clinicalShort: 'K:',
    tele: 'Tele:',
    teleShort: 'T:',
    histology: 'Histologie:',
    histologyShort: 'H:',
    internalClinician: 'Interne Fachkraft',
    externalClinician: 'Externe Fachkraft',
    released: 'Freigegeben',
    unknownDate: 'Unbekanntes Datum',
  },
  patientInfoPage: {
    backToCases: 'Zurück zu den Fällen',
    chooseSpecialityTitle: 'Fachgebiet auswählen, um fortzufahren',
    chooseSpecialitySubtitle:
      'Öffnet die Befundansicht mit dem gewählten Fachgebiet.',
    registrationUnit: '{{count:Registrierung|Registrierungen}}',
    noRegistrationsYetTile: 'Noch keine Registrierungen',
    noSpecialitiesTitle: 'Noch keine Fachgebiete registriert',
    noSpecialitiesSubtitle:
      'Für diesen Patienten sind keine Fachgebiete registriert. Sehen Sie sich die letzten Registrierungen unten an.',
    specialitiesUnavailableTitle: 'Fachgebiete konnten nicht geladen werden',
    specialitiesUnavailableSubtitle:
      'Laden Sie die Seite neu, um es erneut zu versuchen, oder öffnen Sie unten eine letzte Registrierung.',
    orOpenRecentRegistration: 'oder eine letzte Registrierung öffnen',
    columnRegistration: 'Registrierung',
    columnDiagnoses: 'Diagnosen',
    columnActionAndStatus: 'Aktion und Status',
    columnLatestActivity: 'Letzte Aktivität',
    tentative: 'Vorläufig:',
    final: 'Endgültig:',
    awaitingEvaluation: 'Beurteilung ausstehend',
    noRegistrations: 'Noch keine Registrierungen',
    registrationsUnavailable:
      'Registrierungen konnten nicht geladen werden. Laden Sie die Seite neu, um es erneut zu versuchen.',
    openTasksLabel: 'Offene Aufgaben',
    openTasksCount: 'Offene Aufgaben: {count}',
    noOpenTasks: 'Keine offenen Aufgaben',
    openTasksLoading: 'Offene Aufgaben werden geladen…',
    openTasksUnavailable: 'Offene Aufgaben konnten nicht geladen werden',
    taskEconsultsToReview: 'eKonsultationen zur Durchsicht',
    taskDraftRegistrations: 'Registrierungsentwurf',
    taskOverdueFollowUps: 'Überfällige Nachkontrolle',
  },
  statuses: {
    allStatuses: 'Statusüberblick',
    annotated: 'Annotiert',
    approved: 'Genehmigt',
    correct: 'Korrekt',
    incorrect: 'Falsch',
    noFormerAnnotations: 'Keine früheren Annotationen',
    pending: 'Ausstehend',
    overdue: 'Überfällig',
    pendingInvitation: 'Einladung ausstehend',
    unspecified: 'Nicht angegeben',
  },
  statusMessages: {
    anotherLesionBeingEdited: 'Eine andere Läsion wird gerade bearbeitet',
    biopsySuccessfullyClosed: 'Biopsie wurde registriert',
    closeMenuToActivateButton:
      'Schließen Sie das Menü rechts, um diesen Button zu aktivieren',
    copiedToClipboard: 'In die Zwischenablage kopiert',
    diagnosisNotFound: 'Diagnose nicht gefunden',
    invitationAccepted: 'Einladung akzeptiert',
    invitationRejected: 'Einladung abgelehnt',
    invitationSentToEmail: 'Einladung wurde an {email} gesendet',
    lesionEditingDoesNotLiveHere:
      'Die Bearbeitung der Läsion ist hier nicht mehr möglich',
    lesionNotPartOfAnyTask: 'Diese Läsion entält keine Aufgabe',
    lesionNotRegisteredForHistopathologicalEvaluation:
      'Diese Läsion ist nicht für eine histopathologische Einschätzung registriert',
    lesionNotRegisteredForTeledermatologicalEvaluation:
      'Diese Läsion ist nicht für eine dermatologische Einschätzung registriert',
    mustBeValidPhoneNumber:
      'Telefonnummer stimmt nicht mit dem Ländercode überein',
    noClassicCriteriaAvailableForDiagnosis:
      'Keine klassischen Kriterien für diese Diagnose verfügbar',
    noClassicCriteriaSelected: 'Keine klassischen Kriterien ausgewählt',
    patientHasBeenContacted: 'Patient wurde kontaktiert',
    pleaseEnterPhoneNumber: 'Bitte geben Sie Ihre Telefonnummer ein',
    pleaseSelectADeparment:
      'Bitte wählen Sie eine Abteilung aus, um fortzufahren',
    profileUpdated: 'Profil aktualisiert',
    required: 'Erforderlich',
    selectLesionFromSidebar:
      'Bitte wählen Sie eine Läsion in der linken Seitenleiste aus',
    somethingWentWrong: 'Etwas ist schiefgelaufen.',
    successfullySaved: 'Gespeichert',
    teleReferralRequestSent: 'eKonsil wurde gesendet',
    teleReferralSuccessfullyClosed: 'eKonsil wurde abgeschlossen',
    triageSuccessfullyClosed: 'eKonsil abgeschlossen',
    undiagnosableDueToImageQuality:
      'Keine Diagnose möglich aufgrund der Bildqualität',
    userHas14DaysToAccept:
      'Der Benutzer hat 14 Tage Zeit, um die Einladung anzunehmen',
    userRemovedSuccessfully: 'Benutzer erfolgreich entfernt',
    userUpdated: 'Benutzer aktualisiert',
    youAreAboutToLeave: 'Sie sind im Begriff, diese Seite zu verlassen',
  },
  teleAnswerRating: {
    title: 'Wie war diese dermatologische Einschätzung?',
    good: 'Gut',
    bad: 'Schlecht',
    tooltip:
      'Diese Bewertung wird erhoben, um sicherzustellen, dass die dermatologischen Einschätzungen stets von hohem Standard sind. Die Bewertung ist nicht sichtbar für den Dermatologen.',
    goodPlaceholder:
      'Was Ihnen an der dermatologischen Einschätzung gefallen hat…',
    badPlaceholder: 'Verbesserungsbereiche der dermatologischen Einschätzung…',
    successfullyEvaluatedCase: 'Fall erfolgreich bewertet',
  },
  tasks: {
    tabOne: 'Eigene Aufgaben',
    tabTwo: 'Standort Aufgaben',
    action: 'Handlung',
    noTasksHere: 'Sie haben Ihre Aufgabe erledigt!',
    continueToNextTask:
      'Fahren Sie mit der nächsten Aufgabe fort, wenn noch mehr übrig sind.',
    annotationFeedback: {
      description:
        'Annotationen, die überprüft und mit Feedback versehen wurden',
      title: 'Annotation-Feedback',
    },
    annotationsThatNeedReviews: {
      description: 'Annotationen, die noch nicht überprüft wurden',
      title: 'Annotationen überprüfen',
    },
    drafts: {
      description: 'Noch nicht fertiggestellte Entwürfe',
      title: 'Entwürfe',
    },
    biopsies: {
      description: 'Neue Biopsieergebnisse',
      title: 'Biopsien',
    },
    buttons: {
      start: 'Starten',
      viewAll: 'Alle anzeigen',
    },
    menuItem: 'Aufgaben',
    overdueFollowUps: {
      description: 'Befunde mit überfälliger Verlaufskontrolle',
      title: 'Überfällige Nachkontrollen',
    },
    pendingAnnotations: {
      description: 'Trainingsläsionen, die noch nicht annotiert wurden',
      title: 'Ausstehende Annotationen',
    },
    teleAssessments: {
      description: 'Neue dermatologische Einschätzung',
      title: 'eKonsile',
    },
    title: 'Aufgaben',
    total: 'Gesamt',
  },
  teledermatologicalAssessmentToolbar: {
    claimReferralButton: 'eKonsil übernehmen',
    casesSkipped:
      '{count} nicht mehr verfügbare{{s|}} {{eKonsil|eKonsile}} in Ihrer Liste übersprungen',
    errorOccuredWhileClaimingReferral:
      'Beim Übernehmen des eKonsils ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    referralClaimedByOtherUserDescription:
      'Dieses eKonsil wird bereits von einer anderen Fachperson beantwortet.',
    referralNotAnswerableDescription:
      'Sie haben keinen Zugriff, um dieses eKonsil zu beantworten.',
    referralNotClaimedTitle: 'eKonsil noch nicht übernommen',
    referredBy: 'eKonsil gesendet von',
    videoLink:
      'Hier klicken, um ein Video zur optimalen Bildaufnahme anzusehen',
    videoDialogTitle: 'Videos zur Bildverbesserung',
    dermoscopicImageTitle: 'Dermatoskopisches Bild',
    overviewImageTitle: 'Übersichtsbild',
    commentTitle: 'Kommentar',
    commentPlaceholder: 'Geben Sie hier Ihren Kommentar ein...',
    informReferrerAboutAcuteDiagnosis: 'Über akute Diagnose informieren',
    tooltips: {
      imageQualityRatingTitle:
        'Dient zur Schulung des überweisenden Arztes und zur Verbesserung der Bildqualität in Dermloop.',
      teleDiagnosisTitle:
        'Alle gewählten Diagnosen werden in Dermloop gespeichert und können für Statistiken verwendet werden.',
      recommendationsTitle:
        'Empfohlene klinische Handlung für den überweisenden Arzt.',
      commentTitle:
        'Empfehlungen für den überweisenden Arzt: z. B. Behandlungsvorschlag, Erklärung, Schulungstext.',
    },
    timeSpentReviewing: 'Time spent reviewing',
    timeSpentReviewingDescription:
      "A minimum of five minutes reviewing the patient's record and preparing your clinical response is required to bill for CPT code 99451.",
    timeSpentReviewingPlaceholder: 'Time spent reviewing in minutes',
  },
  teleReferralTable: {
    columns: {
      eConsultIdNumber: 'eKonsil-ID',
      status: 'Status',
      clinicalDiagnosis: 'Verdachtsdiagnose',
      teleDermatologicalDiagosis: 'eKonsil Diagnose',
      referrer: 'Überweiser',
      dateOfReferral: 'Datum',
      assessedBy: 'Befundet von',
      assignment: 'Zuweisung',
      favourite: 'Favorit',
    },
    noData: 'Kein eKonsil gefunden',
    placeholders: {
      searchForMedicalUnit: 'Nach Standort suchen',
      primarySearchFieldPlaceHolderInsuranceFlow:
        'Nach Patienten-Nr. oder Name suchen',
    },
  },
  casesTable: {
    noData: 'Keine Fälle gefunden',
    tooltips: {
      cannotCombineFilters:
        'Dieser Filter kann nicht mit der ausgewählten Option kombiniert werden.',
    },
  },
  tooltips: {
    clickToAddNewAreas:
      'Klicken Sie auf das Bild, um neue Bereiche hinzuzufügen',
    dragAndDropToDisplayImage: 'Zum Anzeigen Bild hierher ziehen',
    editUser: 'Benutzer bearbeiten',
    notOnSameLesionAsEditing:
      'Sie befinden sich nicht mehr in derselben Läsion, die gerade bearbeitet wird',
    removeUser: 'Benutzer entfernen',
    userInformationIsMissing: 'Benutzerinformationen fehlen',
    youNeedAtLeastOneAction: 'Bitte wählen Sie mindestens eine Handlung aus',
  },
  unitUsersPage: {
    pageTitle: 'Benutzerverwaltung',
    pageDescription: 'Benutzerinformationen innerhalb von ',
    multiple: 'Mehrere',
    none: 'Keine',
    you: '(Sie)',
    deactivateUser: 'Benutzer deaktivieren',
    back: 'Zurück',
    noData: 'Keine Daten',
    deletedReasonExpired: 'Zugang abgelaufen',
    deletedReasonInactive: 'Seit {days} Tagen inaktiv',
    deletedReasonInactiveGeneric: 'Wegen Inaktivität',
    deletedReasonRemovedByUser: 'Durch Superuser entfernt',
    expired: 'aufgrund abgelaufener Zugriffsrechte',
    inactivity: 'aufgrund von Inaktivität',
    vacationEndsIn: 'Abwesenheit endet in {nrOfDays} Tag{{en}}',
    vacationIn: 'Abwesenheit in {nrOfDays} Tag{{en}}',
    lastActiveToday: 'Zuletzt aktiv heute',
    lastActiveDaysAgo: 'Zuletzt aktiv seit {nrOfDays} Tag{{e}}',
    deactivatedIn: 'Wird deaktiviert in {nrOfDays} Tag{{e}}',
    downloadCSVFailed: 'CSV-Download fehlgeschlagen',
    downloadCSVSuccess: 'CSV erfolgreich heruntergeladen',
    usersPersonalInformation: 'Persönliche Informationen des Benutzers',
    firstName: 'Vorname',
    lastName: 'Nachname',
    email: 'E-Mail',
    phoneNumber: 'Telefonnummer',
    npi: 'NPI',
    specialty: 'Fachgebiet',
    update: 'Aktualisieren',
    updateNotAllowedHere:
      'Bearbeiten ist nur in einer von Ihnen verwalteten Abteilung möglich. Wechseln Sie die Abteilung, um diesen Benutzer zu bearbeiten.',
    userAffiliations: 'Benutzerzuordnungen',
    userAffiliationsDescription:
      'Dies sind die Zuordnungen und Rollen des Benutzers',
    roles: 'Rollen',
    telespecialist: 'Telespecialist',
    resendInvitation: 'Einladung erneut senden',
    copyInvitation: 'Einladungslink kopieren',
    deleteInvitation: 'Einladung löschen',
    exportTrainingLeaderBoard: 'Liste exportieren',
    inactive: 'Deaktiviert',
    deactivatedOn: 'Deaktiviert am',
    reactivateUser: 'Benutzer reaktivieren',
    reactivateUserSorRequired:
      'Benutzer kann nicht reaktiviert werden — SOR-Registrierung ist in dieser Föderation erforderlich',
    invitations: 'Einladungen',
    noPendingInvitations: 'Keine ausstehenden Einladungen',
    editAffiliation: 'Zuordnung bearbeiten',
    editInvitation: 'Einladung bearbeiten',
    expiryDateHelper:
      '* Der Benutzer hat 90 Tage Zeit, um Ihre Einladung anzunehmen.',

    deactivateUserAlert: {
      deactivate: 'Deaktivieren',
      deactivateUserAlertBulletPoint_1:
        'Der Benutzer kann sich weiterhin anmelden und hat ggf. Zugriff auf andere Standorte',
      deactivateUserAlertBulletPoint_2:
        'Der Benutzer bleibt in <>der Benutzerübersicht für {medicalUnitName}<> sichtbar',
      deactivateUserAlertBulletPoint_3:
        'Der Zugriff auf <>{medicalUnitName}<> kann jederzeit wiederhergestellt werden',
      deactivateUserAlertTitle: 'Benutzer deaktivieren?',
      toastMessages: {
        deactivateFailure: 'Benutzer konnte nicht deaktiviert werden',
        deactivateSuccess:
          'Benutzer mit E-Mail {email} wurde erfolgreich deaktiviert',
      },
      willNoLongerHaveAccessTo:
        '<>{email}<> hat keinen Zugriff mehr auf <>{medicalUnitName}<>',
    },

    toastText: {
      successfullyAddedUser: '{email} erfolgreich eingeladen',
      needEmailOrNPI: 'E-Mail oder NPI muss angegeben werden',
      cancel: 'Abbrechen',
      updatedUser: 'Benutzer erfolgreich aktualisiert',
      removedAffiliation: 'Rolle erfolgreich entfernt',
      somethingWentWrong: 'Etwas ist schiefgelaufen',
      copiedLink: 'Einladungslink in Zwischenablage kopiert',
      deletedInvitation: 'Einladung gelöscht',
      resendInvitation: 'Erinnerung erfolgreich gesendet',
      tooManyInvitationReminders:
        'Zu viele Erinnerungen gesendet – nur eine pro Stunde und E-Mail erlaubt',
    },

    roleSheet: {
      title: 'Rolle hinzufügen',
      role: 'Rolle',
      network: 'Netzwerk',
      site: 'Standort',
    },

    tableHeaders: {
      name: 'Nachname, Vorname',
      contact: 'Kontakt',
      association: 'Zugehörigkeit',
      roles: 'Rollen',
      role: 'Rolle',
      status: 'Aktivitätsstatus',
      added: 'Hinzugefügt',
      expiry: 'Ablaufdatum',
    },

    userRolesTableHeaders: {
      networkName: 'Netzwerkname',
      siteName: 'Standortname',
      roles: 'Rolle(n)',
      occupation: 'Beruf',
      specialty: 'Fachgebiet',
      conditionTypes: 'Diagnosetypen',
      activityStatus: 'Aktivitätsstatus',
      added: 'Hinzugefügt',
    },

    conditionTypesField: {
      label: 'Diagnosetypen',
      helperText: 'Leer lassen, um die Diagnosetypen der Klinik zu verwenden.',
      useClinicsConditionTypes: 'Diagnosetypen der Klinik verwenden',
      inherited: 'Diagnosetypen der Klinik — {names}',
      inheritedEmpty: 'Diagnosetypen der Klinik — keine verfügbar',
      editFailed: 'Diagnosetypen konnten nicht gespeichert werden',
      optionsSource:
        'Nur Diagnosetypen, die {federation} anbietet, können gewählt werden.',
      notOffered: '{name} (wird von {federation} nicht mehr angeboten)',
      federationFallback: 'der Föderation',
      loadError:
        'Die Erkrankungsarten der Föderation konnten nicht geladen werden.',
    },

    filters: {
      searchForNameOrEmail: 'Nach Namen oder E-Mail suchen',

      networkFilter: {
        title: 'Netzwerke auswählen',
        allNetworks: 'Alle Netzwerke',
        searchPlaceholder: 'Netzwerk eingeben...',
        selectedNetworks: 'Ausgewählte Netzwerke',
        reset: 'Zurücksetzen',
        defaultTriggerText: 'Alle Netzwerke',
        selectedNetworksTriggerText: 'Netzwerke',
        noValues: 'Keine Netzwerke',
      },

      siteFilter: {
        title: 'Standorte auswählen',
        allSites: 'Alle Standorte',
        searchPlaceholder: 'Standort eingeben...',
        selectedSites: 'Ausgewählte Standorte',
        reset: 'Zurücksetzen',
        defaultTriggerText: 'Alle Standorte',
        selectedSitesTriggerText: 'Standorte',
        noValues: 'Keine Standorte',
      },

      statusFilter: {
        title: 'Status auswählen',
        allStatuses: 'Alle Status',
        selectedStatuses: 'Statusauswahl',
        reset: 'Zurücksetzen',
        selectedStatusesTriggerText: 'Aktivitätsstatus',
        noValues: 'Kein Status',
        defaultTriggerText: 'Aktivitätsstatus: Alle',
        values: {
          active: 'Aktiv',
          deactivated: 'Deaktiviert',
          aboutToBeDeactivated: 'Bald deaktiviert',
          unaffiliated: 'Ohne Zugehörigkeit',
          pendingInvitation: 'Ausstehende Einladung',
          onVacation: 'Abwesenheit',
          subValues: {
            unaffiliated: 'hat nie zu einer medizinischen Einheit gehört',
            active: 'in den letzten {days} Tagen aktiv',
            deactivated:
              'manuell deaktiviert oder seit mehr als {days} Tagen inaktiv',
            aboutToBeDeactivated: 'seit {fromDays}–{toDays} Tagen inaktiv',
          },
        },
      },

      userRolesFilter: {
        title: 'Benutzerrollen auswählen',
        searchPlaceholder: 'Rolle eingeben...',
        allRoles: 'Alle Rollen',
        selectedRoles: 'Ausgewählte Rollen',
        reset: 'Zurücksetzen',
        selectedRolesTriggerText: 'Rollen',
        defaultTriggerText: 'Rollen: Alle',
        noResultsFound: 'Keine Rollen gefunden',
      },

      associationFilter: {
        title: 'Zuordnungen auswählen',
        searchPlaceholder: 'Netzwerk oder Standort eingeben...',
        allAssociation: 'Alle Zuordnungen',
        selectedAssociation: 'Ausgewählte Zuordnungen',
        reset: 'Zurücksetzen',
        selectedAssociationTriggerText: 'Zuordnungen',
        defaultTriggerText: 'Zuordnungen: Alle',
        noResultsFound: 'Keine Zuordnungen gefunden',
      },
    },

    expiringUsersFilter: {
      expiringSoonFilterName: 'Läuft ab: In weniger als 14 Tagen',
      filterValue: 'In weniger als 14 Tagen',
      noFilterValue: 'Alle Benutzer',
      notExpiringSoonFilterName: 'Läuft ab',
    },

    inviteUnitUserDialog: {
      allowedClinicalActions: 'Klinische Handlungen',
      clinician: 'Arzt',
      email: 'E-Mail',
      sendInvitation: 'Einladung senden',
      expireDate: 'Ablaufdatum',
      expireDateExplanation: 'veraltet',
      inviteUser: 'Benutzer einladen',
      medicalUnit: 'Standort',
      medicalUnitDisabled:
        'Dieser Standort kann nicht ausgewählt werden, da dem Benutzer hier bereits eine Rolle zugewiesen ist.',
      npi: 'NPI',
      npiPlaceholder: 'NPI',
      occupation: 'Beruf',
      role: 'Rolle',
      specialty: 'Fachgebiet',
      jurisdictionalGroup: 'Netzwerk',

      errorMessages: {
        invalidEmail: 'Bitte gültige E-Mail mit @ und einem Punkt eingeben.',
        invalidOccupation: 'Bitte einen gültigen Beruf auswählen',
        invalidSpecialty: 'Bitte ein gültiges Fachgebiet auswählen',
        noClinicalActionPicked: 'Bitte klinische Handlung auswählen',
        noDatePicked: 'Bitte ein Ablaufdatum auswählen',
        noEmailEntered: 'Bitte E-Mail-Adresse eingeben',
        noOccupationPicked: 'Bitte einen Beruf auswählen',
        noRolePicked: 'Bitte eine Rolle auswählen',
        noSpecialtyPicked: 'Bitte ein Fachgebiet auswählen',
      },

      placeholders: {
        email: 'email@domain.com',
        medicalUnit: 'Standort auswählen',
        occupation: 'Beruf auswählen',
        role: 'Rolle auswählen',
        specialty: 'Fachgebiet auswählen',
      },
    },

    mergeUsersDialog: {
      title: 'Benutzer zusammenführen',
      description:
        'Identitätsfelder und Zugehörigkeiten von einem Benutzer auf den Hauptbenutzer übertragen. Der Benutzer, von dem migriert wird, wird anschließend deaktiviert.',
      selectUsers: 'Benutzer auswählen',
      selectTargetUser: 'Hauptbenutzer auswählen...',
      selectSourceUser: 'Benutzer auswählen, von dem migriert wird...',
      searchUsers: 'Benutzer suchen...',
      noUsersFound: 'Keine Benutzer gefunden',
      next: 'Weiter',
      back: 'Zurück',
      merge: 'Benutzer zusammenführen',
      cancel: 'Abbrechen',
      sub: 'Sub (primäre Anmeldekennung)',
      oneLoginSub: 'OneLogin Sub',
      kratosId: 'Kratos ID',
      fieldAlreadyMerged:
        'Dieses Feld wurde bereits von einem anderen Benutzer zusammengeführt',
      checkingActivity: 'Klinische Aktivität wird geprüft...',
      loadingUsers: 'Benutzer werden geladen...',
      sourceUserHasActivity:
        'Der Benutzer, von dem migriert wird, hat klinische Aktivität und kann nicht zusammengeführt werden. Gesundheitsakten: {healthRecordCount}, Befunde: {conditionCount}, Triagen: {conditionTriageCount}',
      confirmMerge: 'Zusammenführung bestätigen',
      confirmMergeDescription:
        'Sind Sie sicher, dass Sie diese Benutzer zusammenführen möchten? Diese Aktion kann nicht einfach rückgängig gemacht werden.',
      merging: 'Wird zusammengeführt...',
      mainUser: 'Hauptbenutzer',
      userToMigrateFrom: 'Benutzer, von dem migriert wird',
      selectMainUserFirst: 'Wählen Sie zuerst den Hauptbenutzer',
      createdOn: 'Erstellt {date}',
      userIdShort: 'ID {id}',
      noMedicalUnit: 'Keine medizinische Einheit',
      candidateRestrictionHint:
        'Nur Benutzer, die eine medizinische Einheit mit dem Hauptbenutzer teilen oder überhaupt keiner medizinischen Einheit angehören, können migriert werden.',
      selectIdsToMigrate: 'Zu migrierende IDs auswählen',
      selectIdsToMigrateDescription:
        'Wählen Sie die IDs aus, die vom Benutzer links auf den Hauptbenutzer rechts übertragen werden sollen. Ersetzte IDs werden durchgestrichen, und die an ihre Stelle tretende ID wird grün angezeigt. Sie müssen keine auswählen — die Zugehörigkeit wird in jedem Fall übertragen.',
      newValues: 'Neue Werte für den Hauptbenutzer',
      affiliationWarningTitle: 'Was bei der Zusammenführung übertragen wird',
      affiliationWarning:
        'Die Zugehörigkeit des migrierten Benutzers wird auf den Hauptbenutzer übertragen und im selben Schritt geschlossen, sodass deren Aufzeichnung dem Hauptbenutzer gehört, ohne neuen Zugriff zu gewähren. Nicht abgesendete Entwürfe bleiben bei der geschlossenen Zugehörigkeit, statt in die eigene des Hauptbenutzers zu wandern. Nur Benutzer aus einer der medizinischen Einheiten des Hauptbenutzers oder ganz ohne medizinische Einheit können migriert werden.',
      idTooltips: {
        sub: 'Die primäre Anmeldekennung. Ihr Präfix gibt an, zu welcher Anmeldemethode sie gehört: dkid| ist MitID, 1login| ist 1Login, kratos| ist Kratos und epic| ist Epic.',
        oneLoginSub:
          'Die 1Login-Kennung, die neben der primären Anmeldung erhalten bleibt, damit ein 1Login-Konto nach dem Wechsel zu MitID verbunden bleibt.',
        kratosId: 'Die vom Kratos-Anmeldeablauf verwendete Identitäts-ID.',
      },
      toastMessages: {
        mergeSuccess: 'Benutzer wurden erfolgreich zusammengeführt',
        mergeFailed: 'Zusammenführung fehlgeschlagen: {error}',
      },
    },
    reactivateUserDialog: {
      doNotSetExpirationDate: 'Kein Ablaufdatum festlegen',
      reactivate: 'Reaktivieren',
      specifyExpirationDate:
        'Geben Sie das Ablaufdatum für den Zugriff auf {medicalUnitName} an',
      title: 'Benutzer reaktivieren?',
      toastMessages: {
        reactivateFailed:
          'Etwas ist schiefgelaufen, der Benutzer wurde nicht reaktiviert',
        reactivateSuccess:
          'Die Reaktivierung war erfolgreich und eine neue Einladung wurde an {email} gesendet',
      },
      willBeReactivatedWithPrevAccess:
        '<>{email}<> wird mit klinischem Zugriff auf <>{medicalUnitName}<> reaktiviert',
    },
  },
  specialistGroupsPage: {
    pageTitle: 'Spezialistengruppe',
    createNew: 'Erstelle neue',
    edit: 'Spezialistengruppe bearbeiten',
    back: 'Zurück',
    searchGroups: 'Suche Spezialistengruppe',
    new: 'Neue Spezialistengruppe',
    info: 'Info über Spezialistengruppe',
    name: 'Name',
    description: 'Beschreibung',
    addName: 'Name hinzufügen',
    addDescription: 'Beschreibung hinzufügen',
    update: 'Aktualisieren',
    specialists: 'Spezialisten',
    searchSpecialists: 'Suche nach Spezialistengruppe oder Klinikstandort',
    noSpecialistGroups: 'Keine Spezialistengruppen',
    addSpecialistGroup:
      'Erstelle neue Spezialistengruppen, um sie hier zu sehen.',
    add: 'Hinzufügen',
    specialist: 'Spezialist',
    type: 'Typ',
    allTypes: 'Alle Typen',
    email: 'Email',
    phoneNumber: 'Telefonnummer',
    affiliations: 'Zugehörigkeiten',
    allAffiliations: 'Alle Zugehörigkeiten',
    noSpecialists: 'Keine Spezialisten in dieser Gruppe',
    addSpecialists:
      'Fügen Sie Spezialisten oder eine Praxis hinzu, um sie hier zu sehen.',
    specialistGroupAffiliations: 'Zugehörigkeiten der Spezialistengruppe',
    searchAffiliations: 'Suche nach Standort oder Netzwerk',
    noAffiliations: 'Keine Zugehörigkeiten gefunden',
    addAffiliations:
      'Fügen Sie diese Spezialistengruppe zu einer oder mehreren Praxen oder Netzwerken hinzu, um sie hier zu sehen.',
    addSpecialist: 'Spezialisten hinzufügen',
    selectedSpecialists: 'Ausgewählte Spezialisten',
    clinicalSite: 'Klinikstandort',
    network: 'Netzwerk',
    preferredSpecialists: 'Bevorzugte Spezialisten',
    allowedSpecialists: 'Zulässige Spezialisten',
    openUserTable: 'Öffnen in Benutzertabelle',
    remove: 'Von Liste entfernen',
    moreOptions: 'Weitere Optionen',
    nameAlreadyExists:
      'Es gibt bereits eine Spezialistengruppe mit diesem Namen',
    assignmentNotificationMode: 'E-Mails bei neuer Zuweisung',
    assignmentNotificationModeDescription:
      'Legen Sie fest, wann Mitglieder eine E-Mail zu einem neu zugewiesenen Fall erhalten.',
    assignmentNotificationModes: {
      EveryAssignment: {
        label: 'Bei jeder Zuweisung',
        description:
          'Den zugewiesenen Spezialisten zu jedem Fall per E-Mail benachrichtigen.',
      },
      Standard: {
        label: 'Standard',
        description:
          'Nur benachrichtigen, wenn der Spezialist keine anderen unbeantworteten Fälle hat und in den letzten 2 Stunden keine E-Mail erhalten hat.',
      },
      Off: {
        label: 'Aus',
        description:
          'Nie über neue Zuweisungen benachrichtigen. Erinnerungen und Warnungen vor einer Neuzuweisung werden weiterhin gesendet.',
      },
    },
    toasts: {
      created: 'Spezialistengruppe erstellt',
      updated: 'Spezialistengruppe aktualisiert',
      specialistAssigned: 'Spezialist zur Gruppe hinzugefügt',
    },
  },
  medicalUnitsPage: {
    pageTitle: 'Standorte',
    createNew: 'Hinzufügen',
    edit: 'Klinik bearbeiten',
    back: 'Zurück',
    new: 'Neuer Standort',
    info: 'Standortinformation',
    noSites: 'Keine Kliniken',
    addSites: 'Erstellen Sie eine neue Klinik.',
    searchSites: 'Suchen nach Klinik',
    name: 'Name',
    addName: 'Name hinzufügen',
    jurisdictionalGroup: 'Netzwerk',
    defaultActions: 'Standardaktionen für eingeladene Benutzer',
    addJurisdictionalGroup: 'Netzwerk wählen',
    conditionTypes: 'Erkrankungsarten',
    conditionTypesCaption:
      'Zeigt die Erkrankungsarten, die {federation} anbietet. Nur Melatech kann ändern, welche Arten eine Föderation anbietet.',
    conditionTypeNotOffered:
      'Wird von {federation} nicht mehr angeboten, zum Entfernen abwählen',
    federationFallback: 'der Föderation',
    conditionTypesLoadError:
      'Die Erkrankungsarten der Föderation konnten nicht geladen werden.',
    dermatoscope: 'Dermatoskop',
    description: 'Beschreibung',
    addDescription: 'Beschreibung hinzufügen',
    onboardingDate: 'Onboarding-Datum Dermloop',
    selectDate: 'Datum auswählen',
    add: 'Hinzufügen',
    seeMore: 'Mehr anzeigen',
    seeLess: 'Weniger anzeigen',
    otherDetails: {
      title: 'Kontaktangaben',
      address: 'Adresse',
      addAddress: 'Adresse hinzufügen',
      city: 'Stadt',
      addCity: 'Stadt hinzufügen',
      state: 'Bundesland',
      addState: 'Bundesland hinzufügen',
      zipCode: 'Postleitzahl',
      addZipCode: 'Postleitzahl hinzufügen',
      phoneNumber: 'Telefonnummer',
      addPhoneNumber: 'Telefonnummer hinzufügen',
      fax: 'Faxnummer',
      addFax: 'Faxnummer hinzufügen',
      email: 'E-Mail',
      addEmail: 'E-Mail hinzufügen',
    },
    workflows: 'Dermatologie workflows',
    lesions: {
      title: 'Läsion',
      description:
        'Läsionen sind standardmäßig immer aktiviert. Diese Funktion kann nicht deaktiviert werden.',
    },
    rashes: {
      title: 'Ausschläge',
      description:
        'Ermöglicht die Erfassung von Ausschlägen und den vollständigen Workflow für diese Fälle in Dermloop.',
    },
    lesionThickness: {
      title: 'Läsionsdicke verwenden',
      description:
        'Wenn aktiviert, wird der Kliniker bei der Erstellung einer Läsion aufgefordert, die Breslow-Dicke zu schätzen, falls Melanom als Verdachtsdiagnose ausgewählt wurde.',
    },
    xdis02Messages: {
      title: 'XDIS02: Entlassungsbriefe',
      description:
        'Sendet einen Entlassungsbrief an das Praxisinformationssystem des überweisenden Arztes über das dänische Gesundheitsdatennetzwerk, wenn ein Spezialist eine Beurteilung abschließt.',
    },
    xdis91Messages: {
      title: 'XDIS91: Korrespondenzen',
      description:
        'Sendet eine Korrespondenz an das Praxisinformationssystem des Benutzers mit einer Kopie der Patientenakte, wenn die entsprechende Schaltfläche in der Dermloop-Patientenakte angeklickt wird.',
    },
    specialistManagement: {
      title: 'Spezialistenverwaltung',
      description:
        'Ermöglicht die Konfiguration, wie Spezialisten Fälle auf Klinikebene bearbeiten.',
    },
    providerId: 'Nutzer-ID',
    locationCode: 'Standortcode',
    cvr: 'CVR',
    created: 'Erstellt am',
    noUsers: 'Keine Benutzer in dieser Klinik',
    addUsers: 'Fügen Sie Benutzer hinzu, um sie hier aufgelistet zu sehen.',
    update: 'Aktualisieren',
    selectSpecialistGroup: 'Spezialistengruppe auswählen',
    tootlips: {
      allowedSpecialists:
        'Zugelassene Spezialisten sind als Nächste an der Reihe, einen Fall zu erhalten, wenn keine bevorzugten Spezialisten reagieren. Sie erhalten Fälle erst, nachdem die vollständige Rotation in der bevorzugten Gruppe abgeschlossen ist.',
      preferredSpecialists:
        'Bevorzugte Spezialisten erhalten einen Fall zuerst. Wenn keiner reagiert, wird der Fall an die Gruppe der zugelassenen Spezialisten weitergeleitet.',
    },
    specialistsAlert: {
      title: 'Deaktivér specialiststyring?',
      description:
        'Wenn Sie die Spezialistensteuerung deaktivieren, werden Ihre ausgewählten Spezialistengruppen entfernt. Sind Sie sicher, dass Sie fortfahren möchten?',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
    },
    sorAlert: {
      title: 'Fehlende SOR-ID',
      description:
        'Eine gültige Klinik muss eine SOR-ID haben. Sind Sie sicher, dass Sie ohne SOR-ID fortfahren möchten?',
      cancel: 'Abbrechen',
      confirm: 'Fortfahren',
    },
    deleteSorAlert: {
      title: 'SOR-Informationen löschen?',
      description:
        'Sind Sie sicher, dass Sie die SOR-Informationen von dieser Klinik entfernen möchten? Dies löscht auch die Anbieter-ID.',
      cancel: 'Abbrechen',
      confirm: 'Löschen',
    },
    toasts: {
      created: 'Klinik erstellt',
      updated: 'Klinik aktualisiert',
    },
  },

  jurisdictionalGroupPage: {
    pageTitle: 'Netzwerk',
    createNew: 'Hinzufügen',
    edit: 'Netzwerk bearbeiten',
    back: 'Zurück',
    new: 'Neues Netzwerk',
    info: 'Netzwerk Info',
    noNetworks: 'Keine Netzwerke',
    addNetworks:
      'Erstellen Sie ein neues Netzwerk, um sie hier aufgelistet zu sehen.',
    searchNetworks: 'Nach Netzwerknamen suchen',
    name: 'Name',
    addName: 'Name des Netzwerks',
    description: 'Beschreibung',
    addDescription: 'Beschreibung hinzufügen',
    insurancePayer: 'Versicherung',
    insurancePayerDescription:
      'Dies ist die Liste der mit dem Netzwerk verbundenen Kostenträgern. Sie steht Benutzern mit Berechtigungen zur Versicherungsverwaltung zur Verfügung.',
    insurancePayerName: 'Versicherung',
    newProvider: 'Neue Versicherung hinzufügen',
    numberOfClinicalSites: 'Anzahl Praxen',
    numberOfUsers: 'Anzahl Benutzer',
    scanner: 'ID Scanner',
    fax: 'Fax',
    addFaxNumber: 'Faxnummer hinzufügen',
    openSite: 'Standort öffnen',
    filters: {
      scannerFilter: {
        title: 'Scanner',
        defaultTriggerText: 'Alle Scanner',
        selectedScannersTriggerText: 'Ausgewählte Scanner',
        reset: 'Zurücksetzen',
        selectedScanners: 'Ausgewählt',
        allScanners: 'Alle Scanner',
        noValues: 'Keine Scanner',
        searchPlaceholder: 'Scanner suchen',
      },
      createdOnFilter: 'Erstellt am',
    },
    toasts: {
      created: 'Netzwerk erstellt',
      updated: 'Netzwerk aktualisiert',
    },
    scannerLabels: {
      ssn: 'CPR-Scanner',
      noScanner: 'Manuelle Eingabe',
    },
    selfReactivation: 'Selbst-Reaktivierung',
    selfReactivationDescription:
      'Inaktiven Benutzern erlauben, ihre Konten selbst zu reaktivieren.',
    trustedDomainMfaSkip: 'Telefonbestätigung überspringen',
    trustedDomainMfaSkipDescription:
      'Den SMS-Code für Benutzer überspringen, die sich mit einem per E-Mail gesendeten Link oder Code anmelden.',
    emailDomainOptionsHint:
      'Diese Optionen gelten nur für Benutzer, deren E-Mail eine erlaubte Domain hat. Fügen Sie eine Domain hinzu, um sie zu aktivieren.',
    allowedEmailDomain: 'Erlaubte E-Mail-Domain',
    allowedEmailDomainPlaceholder: 'ihre-domain.io',
    invalidEmailDomain: 'Ungültiges E-Mail-Domänenformat',
    addEmailDomain: 'E-Mail-Domain hinzufügen',
    sendFaxOnReferralClosed:
      'Fax senden, wenn das eConsult vom Versicherungsmanager geschlossen wird',
    pdfDownloadEnabled: '„PDF herunterladen“ anzeigen',
    displayEligiblePayers:
      'Berechtigte Kostenträger auf der Startseite anzeigen',
    ehrManagedActionsEnabled: 'EHR-verwaltete Aktionen',
    ehrManagedActionsEnabledDescription:
      'Wenn aktiviert, werden klinische Aktionen vom verbundenen EHR-System (z. B. Epic) verwaltet. Das Erstellen neuer Aktionen direkt in Dermloop ist ohne einen Encounter-Kontext aus dem EHR-System deaktiviert.',
    save: 'Speichern',
  },

  warningMessages: {
    areYouSureYouWantToDeleteAnnotation:
      'Möchten Sie die Annotation für {annotation} wirklich löschen?',
    changesWillBeLost: 'Ihre Änderungen gehen verloren. Sind Sie sicher?',
    lesionAlreadyHasAnApprovedAnnotation:
      'Diese Trainingsläsion hat bereits eine genehmigte Annotation. Alle vorherigen Genehmigungen entfernen und diese Annotation genehmigen?',
    unsavedChanges:
      'Sie haben ungespeicherte Änderungen. Möchten Sie wirklich fortfahren?',
    unsavedChangesNavigation:
      'Sie haben ungespeicherte Änderungen. Abschließen oder verwerfen Sie sie, um fortzufahren.',
    youAreAboutToUnapprove:
      'Sie sind dabei, diese Annotation abzulehnen. Fortfahren?',
  },

  emailOTP: {
    title: 'E-Mail verifizieren',
    enterOTP: 'Geben Sie den an Ihre E-Mail gesendeten Code ein',
    description:
      'Wir haben einen Verifizierungscode an folgende E-Mail gesendet: {email}',
    verify: 'Verifizieren',
    sendNewCode: 'Neuen Code senden',
    otpIsRequired: 'Ein gültiger Verifizierungscode ist erforderlich',
    resetEmail: 'E-Mail zurücksetzen',
    toasts: {
      emailResetSuccess:
        'E-Mail erfolgreich zurückgesetzt, bitte geben Sie eine neue E-Mail-Adresse an',
      error: 'Etwas ist schiefgelaufen',
      wrongCode: 'Falscher Code',
      success: 'E-Mail verifiziert',
      errorResending: 'Verifizierungscode konnte nicht erneut gesendet werden',
      successResending: 'Neuer Verifizierungscode wurde erfolgreich erstellt',
    },
  },

  vacation: {
    vacation: 'Urlaub',
    helpMessage:
      'Geben Sie hier Ihren Urlaubszeitraum an, damit während Ihrer Abwesenheit keine eConsults zugewiesen werden.',
    helpMessageAdmin:
      'Geben Sie hier den Urlaubszeitraum für diesen Benutzer an, damit während der Abwesenheit keine eConsults zugewiesen werden.',
    noVacations: 'Sie haben noch keinen Urlaub angegeben.',
    from: 'Von',
    until: 'Bis',
    addVacation: 'Urlaub hinzufügen',
    editVacation: 'Urlaub bearbeiten',
    deleteVacation: 'Urlaub löschen?',
    successfullyDeleted: 'Urlaub gelöscht',
    errorDeletion: 'Fehler beim Löschen des Urlaubs',
    successfullyUpdated: 'Urlaub gespeichert',
    errorUpdating: 'Fehler beim Aktualisieren des Urlaubs',
    successfullyCreated: 'Urlaub hinzugefügt',
    errorCreating: 'Fehler beim Hinzufügen des Urlaubs',
    fromDateRequired: 'Von-Datum ist erforderlich',
    untilDateRequired: 'Bis-Datum ist erforderlich',
    dates: 'Urlaubszeiträume',
    selectDates: 'Urlaubszeiträume auswählen...',
    datesRequired: 'Urlaubszeiträume sind erforderlich',
  },
  accountDelegation: {
    error:
      'Ein Fehler ist aufgetreten. Schließen Sie den Dialog und öffnen Sie ihn erneut oder wenden Sie sich an den Support, wenn das Problem weiterhin besteht.',
    noValidExchangeToken:
      'Es konnte keine gültige QR-Code-Sitzung gefunden werden.',
    linkText: 'Im Browser öffnen',
    refreshTooltip: 'Klicken Sie, um einen neuen QR-Code zu generieren',
    refreshButtonText: 'QR-Code aktualisieren',
    generateNewQRCode: 'Neuen QR-Code generieren',
    or: 'oder',
    linkOnlyTitle: 'Willkommen bei Dermloop',
    linkOnlyDescription:
      'Greifen Sie auf Ihr Dermloop-Konto zu, um Patientenfälle zu überprüfen.',
    linkOnlyButtonText: 'Dermloop öffnen',
    linkOnlyFootnote: 'Dermloop wird in einem neuen Browser-Tab geöffnet.',
    copiedToClipboard: 'Kopiert!',
  },

  conditionPdf: {
    title: '{name} Bericht',
    conditionId: 'Fall-Nr.',
    patientId: 'Patienten-Nr.',
    name: 'Patientenname',
    dob: 'Geburtsdatum',
    linkTitle: 'Hier sehen Sie den Bericht mit Bildern in voller Auflösung:',
    pageCount: 'Seite {currentPage} von {totalPages}',
    overviewImages: 'Übersichtsbilder',
    closeUpImages: 'Nahaufnahmen',
    dermoscopicImages: 'Dermatoskopische Bilder',
    images: 'Bilder',
    pcs: 'stk',
    seeAll: 'Alle anzeigen',
    yearOld: 'Jahre alt',
    success: 'PDF erfolgreich heruntergeladen',
    button: {
      downloadPdf: 'PDF herunterladen',
      downloadAll: 'Gesamte Fallakte',
      downloadLatest: 'Neueste Beurteilung',
    },
  },

  responseSidebar: {
    successfullyReviewed: 'Fall als geprüft markiert',
    footer: {
      button: {
        notReviewed: 'Als gelesen markieren',
        submitEvaluation: 'Bewertung abschicken',
        reviewed: 'Bewertet',
      },
      description: {
        notReviewed:
          'Speichert Ihre Notiz und entfernt den Vorgang von Ihrer Aufgabenliste.',
        reviewed:
          'Der Fall wurde als gelesen markiert und aus Ihrer Aufgabenliste entfernt.',
        followupQuestionSent:
          'Der Fall wurde als gelesen markiert, da Sie eine Nachfrage gestellt haben.',
      },
      notePlaceholder: {
        label: 'Notiz hinzufügen (optional)',
        reviewed:
          'Der Fall wurde als gelesen markiert und aus Ihrer Aufgabenliste entfernt.',
        followupQuestionSent:
          'Das Notizfeld ist gesperrt, da Sie eine Folgefrage gestellt haben.',
        notReviewed: 'Verfassen Sie eine Notiz für den Fall in Dermloop...',
      },
    },
    responseReceipt: {
      disposition: 'Weiteres Vorgehen',
      actionUnavailable: 'Diese Maßnahme wird im Formular nicht mehr angeboten',
      couldNotDisplay:
        'Die Facharztantwort konnte nicht angezeigt werden. Bitte wenden Sie sich an den Support.',
    },
    specialistResponse: {
      title: 'Fachliche Antwort',
      note: 'Fachhinweis',
      evaluatedBy: 'Befundet von',
      recommendation: 'Empfehlung',
      teleDiagnosis: 'Tele-Diagnostik',
      secondaryDiagnosis: 'Differentialdiagnose',
      tertiaryDiagnosis: 'Zweite Differentialdiagnose',
      differentialDiagnosis: 'Differentialdiagnose',
    },
    imageQualityAssessmentResponse: {
      videoLink:
        'Hier klicken, um ein Video zur optimalen Bildaufnahme anzusehen',
      videoDialogTitle: 'Videos zur Bildverbesserung',
      dermoscopicImageVideoTitle: 'Dermatoskopisches Bild',
      overviewImageVideoTitle: 'Übersichtsbild',
      title: 'Bewertung der Bildqualität',
    },
    errorMessages: {
      badEvaluationRequiresNote:
        'Eine schlechte Bewertung erfordert einen Kommentar.',
    },
    followUpQuestion: {
      title: 'Stellen Sie dem Spezialisten eine Anschlussfrage.',
      placeholder:
        'Falls Sie eine Anschlussfrage an den Spezialisten haben, können Sie diese hier eingeben und über die Schaltfläche unten absenden.',
      submitText: 'Senden Sie eine Anschlussfrage',
      followUpQuestionSubmitted: 'Die Anschlussfrage wurde gesendet.',
      sent: {
        title: 'Ihre Rückfrage wurde nun an den Spezialisten gesendet.',
        question: 'Frage:',
        youWillBeNotified:
          'Sie werden in Form einer neuen Tele-Beurteilung benachrichtigt, sobald der Spezialist Ihre Anfrage beantwortet hat.',
      },
    },
    patientExplanation: {
      label: 'Patientengerichtete Erklärung',
      description:
        'Der Text wird dem Patienten als Erklärung und Empfehlung zur Beurteilung des Dermatologen angezeigt. Wenn das Feld leer ist, wird kein Text gesendet.',
      placeholder:
        'Erklärung und Empfehlung für den Patienten eingeben (optional)',
    },
  },

  patientResponse: {
    supportedByDermloop: 'Unterstützt von Dermloop',
    evaluationIntendedFor: 'Diese Beurteilung ist bestimmt für',
    dateOfAssessment: 'Datum der Beurteilung',
    dermatologistAssessment: 'Beurteilung des Dermatologen',
    explanationAndAdvice: 'Erklärung und Empfehlung',
    privacyAvailableUntil:
      'Zum Schutz Ihrer Privatsphäre ist diese Beurteilung nur verfügbar bis:',
    questions: 'Fragen?',
    contactMessage: 'Bei Fragen können Sie sich an {organizationName} wenden.',
    sentViaSecureLink: 'Gesendet über einen sicheren Link',
    verifyToViewResults: 'Verifizieren Sie sich, um Ihre Ergebnisse anzuzeigen',
    enterIdentifierToAccess:
      'Geben Sie Ihre {identifierType|{ssn:Sozialversicherungsnummer, phoneNumber:Telefonnummer}} ein, um sicher auf Ihre Ergebnisse zuzugreifen',
    enterIdentifier:
      '{identifierType|{ssn:Sozialversicherungsnummer, phoneNumber:Telefonnummer}} eingeben',
    linkNoLongerActive: 'Dieser Link ist nicht mehr aktiv',
    linkExpiredMessage:
      'Dieser Link ist abgelaufen, um Ihre Privatsphäre und Datensicherheit zu schützen',
    error: 'FEHLER!',
    invalidIdentifier:
      'Ungültige Kennung. Bitte überprüfen und erneut versuchen.',
    tooManyAttempts: 'Zu viele Versuche. Kontaktieren Sie Ihre Klinik.',
    loading: 'Laden...',
    submit: 'Absenden',
  },
  support: {
    buttonTooltip: 'Support kontaktieren',
    title: 'Womit können wir Ihnen helfen?',
    description: 'Fragen, Feedback, Probleme oder Ideen. Wir lesen alles.',
    step1Label: 'Soll der Fall mitgeschickt werden?',
    step2Label: 'Beschreibung',
    step3Label: 'Wie möchten Sie kontaktiert werden?',
    descriptionPlaceholder:
      'Was möchten Sie uns mitteilen? Womit brauchen Sie Hilfe? Oder haben Sie eine Idee für eine neue Funktion?',
    descriptionRequired: 'Bitte beschreiben Sie, wobei Sie Hilfe benötigen',
    switchToDetailed:
      'Gehen Sie zu einem Fall und öffnen Sie den Support erneut, um den Fall mitzusenden',
    notOnCasePage: 'Sie sind gerade auf keinem Fall',
    yes: 'Ja',
    no: 'Nein',
    contactMethodEmail: 'E-Mail',
    contactMethodPhone: 'Telefon',
    emailLabel: 'E-Mail-Adresse',
    emailPlaceholder: 'ihre.email@example.com',
    emailRequired: 'Bitte geben Sie eine E-Mail-Adresse an',
    emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse an',
    phoneLabel: 'Telefonnummer',
    phonePlaceholder: '+49 1234 2345678',
    phoneRequired: 'Bitte geben Sie eine Telefonnummer an',
    phoneInvalid: 'Bitte geben Sie eine gültige Telefonnummer an',
    orContactDirectly: 'Oder kontaktieren Sie uns direkt',
    callCustomerSupport: 'Rufen Sie uns an',
    callSupportDescription: 'Rufen Sie uns an, wir helfen Ihnen weiter',
    phoneNumber: '+45 44 40 82 45',
    openingHours: 'Mo-Fr 08:00-16:00',
    cancelButton: 'Abbrechen',
    sendRequestButton: 'Anfrage senden',
    confirmationTitle: 'Danke, dass Sie uns geschrieben haben!',
    ticketId: 'Ticket-ID:',
    confirmationMessage: 'Wir melden uns so schnell wie möglich zurück.',
    updatesVia: 'Sie hören von uns über',
    updatesOn: 'Sie hören von uns unter',
    closeButton: 'Schließen',
    toastError:
      'Nachricht konnte nicht gesendet werden — bitte erneut versuchen. Das Dermloop-Team wurde benachrichtigt.',
  },
} satisfies NamespaceDesktopWebTranslation;

export default de_desktop_web;
